import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
    Box,
    Stack,
    Heading,
    Dropdown,
    TextField,
    Checkbox,
    Button,
    Columns,
    Column,
    Card,
    Inline,
    Text,
    RadioGroup,
    RadioItem
} from "braid-design-system";
import { usePageHeading } from "../contexts/PageHeadingProvider";
import { useToast } from "../hooks/useToast";
import printPolicyService from "../services/printPolicyService";
import { getAccountTypeCategories, PlAccountTypeCategory } from "../services/plAccountTypeService";
import {
    getAllTransactionTypes,
    getTransactionTypePrintConfig,
    saveTransactionTypePrintConfig,
    ItTransactionTypeResponse
} from "../services/transactionService";
import { defaultPrintConfig } from "../config/defaultPrintConfig";

interface DesignerField {
    id: string;
    label: string;
    isCustom?: boolean;
}



const formatFontSize = (sz: any): string => {
    if (!sz) return 'inherit';
    const trimmed = String(sz).trim();
    if (!trimmed) return 'inherit';
    if (/^\d+$/.test(trimmed)) {
        return `${trimmed}px`;
    }
    return trimmed;
};

const formatGlobalFontSize = (sz: any): string => {
    if (!sz) return '12px';
    const trimmed = String(sz).trim();
    if (!trimmed) return '12px';
    if (/^\d+$/.test(trimmed)) {
        return `${trimmed}px`;
    }
    return trimmed;
};

const getAvailableTableColumns = (_pType: string, cat: string) => {
    if (cat === "PAWNING_MANDATE") {
        return [
            { id: "name", label: "Name/Desc" },
            { id: "description", label: "Description" },
            { id: "quantity", label: "Qty" },
            { id: "carat", label: "Carat" },
            { id: "weight_gram", label: "Weight (g)" },
            { id: "gold_weight_gram", label: "Gold Wt (g)" },
            { id: "accessed_value", label: "Assessed Val" },
            { id: "market_value", label: "Market Val" },
            { id: "capital", label: "Capital" }
        ];
    }
    return [
        { id: "account_number", label: "Acc No" },
        { id: "account_name", label: "Acc Name" },
        { id: "amount", label: "Amount" },
        { id: "balance", label: "Balance" },
        { id: "debit", label: "Debit" },
        { id: "credit", label: "Credit" },
        { id: "interest", label: "Interest" },
        { id: "penalty", label: "Penalty" },
        { id: "fund", label: "Fund" },
        { id: "passdue", label: "Pass Due" },
        { id: "description", label: "Description" },
        { id: "cheque_num", label: "Cheque No" },
        { id: "unit_price", label: "Unit Price" },
        { id: "qty", label: "Qty" }
    ];
};

const getDefaultTableColumns = (_pType: string, cat: string) => {
    if (cat === "PAWNING_MANDATE") {
        return ["name", "description", "quantity", "carat", "weight_gram", "gold_weight_gram", "accessed_value", "market_value", "capital"];
    }
    return ["account_number", "account_name", "amount", "interest", "penalty", "fund", "passdue", "balance"];
};

const PrintDesigner: React.FC = () => {
    const { setPageHeading } = usePageHeading();
    const toast = useToast();

    // Standard available fields definitions
    const commonCustomerAndAccountFields: DesignerField[] = [
        { id: "open_time", label: "Open Time" },
        { id: "interest_rate", label: "Interest Rate" },
        { id: "interest_rate_by_12", label: "Interest Rate / 12" },
        { id: "branch_name", label: "Branch Name" },
        { id: "branch_address", label: "Branch Address" },
        { id: "institute_name", label: "Institute Name" },
        { id: "institute_address", label: "Institute Address" },
        { id: "institute_phone_number", label: "Institute Phone Number" },
        { id: "institute_email", label: "Institute Email" },
        { id: "institute_reg_num", label: "Institute Registration No" },
        { id: "institute_reg_date", label: "Institute Registration Date" },
        { id: "branch_telephone_number", label: "Branch Telephone Number" },
        { id: "branch_email", label: "Branch Email" },
        { id: "member_number", label: "Member Number" },
        { id: "mobile_number", label: "Mobile Number" },
        { id: "token", label: "Customer Token" },
        { id: "latitude", label: "Latitude" },
        { id: "longitude", label: "Longitude" },
        { id: "postal_code", label: "Postal Code" },
        { id: "gender", label: "Gender" },
        { id: "married_status", label: "Married Status" },
        { id: "home_phone", label: "Home Phone" },
        { id: "credit_limit", label: "Credit Limit" },
        { id: "credit_limit_date", label: "Credit Limit Date" },
        { id: "inactive_reson", label: "Inactive Reason" },
        { id: "sms_status", label: "SMS Status" },
        { id: "dp_app_status", label: "DP App Status" },
        { id: "pos_app_status", label: "POS App Status" },
        { id: "joint_customer", label: "Joint Customer" },
        { id: "interest_update_account", label: "Interest Update Account" },
        { id: "reneve_date", label: "Renew Date" },
        { id: "approved", label: "Approved Amount" },
        { id: "lowest_balance", label: "Lowest Balance" },
        { id: "minimum_balance", label: "Minimum Balance" },
        { id: "no_of_interest_pay", label: "No. of Interest Payments" },
        { id: "capital_installment", label: "Capital Installment" },
        { id: "total_installment", label: "Total Installment" },
        { id: "save_interest_temp", label: "Save Interest Temp" },
        { id: "penalty_rate", label: "Penalty Rate" },
        { id: "insurance_rate", label: "Insurance Rate" },
        { id: "fee_rate", label: "Fee Rate" },
        { id: "due_interest", label: "Due Interest" },
        { id: "penalty", label: "Penalty" },
        { id: "fund", label: "Fund" },
        { id: "insurance", label: "Insurance" },
        { id: "fee", label: "Fee" },
        { id: "past_due", label: "Past Due" },
        { id: "past_due_days", label: "Past Due Days" },
        { id: "past_due_amount", label: "Past Due Amount" },
        { id: "pb_page_no", label: "Passbook Page No" },
        { id: "pb_line_no", label: "Passbook Line No" },
        { id: "pb", label: "Passbook Tracking" },
        { id: "standing_order_amount", label: "Standing Order Amount" },
        { id: "tax", label: "Tax" },
        { id: "tax_rate", label: "Tax Rate" },
        { id: "mobile_pos_update", label: "Mobile POS Update" },
        { id: "last_transaction_date", label: "Last Transaction Date" },
        { id: "withdraw_instruction", label: "Withdraw Instruction" },
        { id: "installment_status", label: "Installment Status" },
        { id: "allow_interest_edit", label: "Allow Interest Edit" },
        { id: "allow_other_branch", label: "Allow Other Branch" },
        { id: "opening_date", label: "Opening Date" },
        { id: "opening_balance", label: "Opening Balance" },
        { id: "previous_account_no", label: "Previous Account No" },
        { id: "original_interest", label: "Original Interest" },
        { id: "interest_installment", label: "Interest Installment" },
        { id: "first_total_installment", label: "First Installment Total" },
        { id: "first_capital_installment", label: "First Installment Capital" },
        { id: "first_interest_installment", label: "First Installment Interest" },
        { id: "first_installment_date", label: "First Installment Date" },
        { id: "last_total_installment", label: "Last Installment Total" },
        { id: "last_capital_installment", label: "Last Installment Capital" },
        { id: "last_interest_installment", label: "Last Installment Interest" },
        { id: "last_installment_date", label: "Last Installment Date" },
        { id: "interest_balance", label: "Interest Balance" },
        { id: "total_interest", label: "Total Interest" },
        { id: "days_for_loan_int", label: "Days for Loan Interest" },
        { id: "print_time", label: "Print Time" },
        { id: "print_date", label: "Print Date" },
        { id: "c_by", label: "Created By ID" },
        { id: "c_by_name", label: "Created By Name" },
        { id: "voucher_number", label: "Voucher Number" },
        { id: "description", label: "Description" }
    ];

    const mergeFields = (base: DesignerField[]): DesignerField[] => {
        const ids = new Set(base.map(f => f.id));
        const toAdd = commonCustomerAndAccountFields.filter(f => !ids.has(f.id));
        return [...base, ...toAdd];
    };

    const passbookFrontFields: DesignerField[] = mergeFields([
        { id: "account_number", label: "Account Number" },
        { id: "customer_number", label: "Customer Number" },
        { id: "account_type_name", label: "Account Type Name" },
        { id: "customer_address", label: "Customer Address" },
        { id: "customer_mobile_number", label: "Customer Mobile Number" },
        { id: "customer_nic", label: "Customer NIC" },
        { id: "open_date", label: "Opening Date" },
        { id: "period", label: "Period" },
        { id: "period_minus_one", label: "Period - 1" },
        { id: "nominee_name", label: "Nominee Name" },
        { id: "title", label: "Customer Title" },
        { id: "full_name", label: "Customer Full Name" },
        { id: "ini_name", label: "Customer Name with Initials" },
        { id: "address", label: "Customer Address" },
        { id: "date_of_birth", label: "Date of Birth" },
        { id: "member_date", label: "Member Date" },
        { id: "mobile_2", label: "Mobile 2" },
        { id: "e_mail", label: "Email" },
        { id: "reprint", label: "Re-Print" }
    ]);

    const fdCertificateFields: DesignerField[] = mergeFields([
        { id: "institute_name", label: "Institute Name" },
        { id: "open_date", label: "Date of Deposit" },
        { id: "closing_date", label: "Date of Maturity" },
        { id: "ref_account_number", label: "Account Number" },
        { id: "period", label: "Period" },
        { id: "balance", label: "Balance" },
        { id: "balance_words", label: "Balance (Words)" },
        { id: "interest_rate", label: "Interest Rate" },
        { id: "interest_rate_period", label: "Interest Rate & Period" },
        { id: "customer_nic", label: "NIC" },
        { id: "full_name", label: "Customer Full Name" },
        { id: "address", label: "Customer Address" },
        { id: "mobile_1", label: "Mobile Number" },
        { id: "reprint", label: "Re-Print" }
    ]);

    const pawningMandateFields: DesignerField[] = mergeFields([
        { id: "balance", label: "Account Balance" },
        { id: "balance_words", label: "Account Balance (Words)" },
        { id: "open_date", label: "Opening Date" },
        { id: "ref_account_number", label: "Reference Account Number" },
        { id: "note", label: "Account Note" },
        { id: "customer_number", label: "Customer Number" },
        { id: "full_name", label: "Customer Full Name" },
        { id: "address", label: "Customer Address" },
        { id: "nic", label: "NIC" },
        { id: "quantity", label: "Item Quantity" },
        { id: "carat", label: "Carat Value" },
        { id: "weight_gram", label: "Weight (g)" },
        { id: "gold_weight_gram", label: "Gold Weight (g)" },
        { id: "accessed_value", label: "Accessed Value" },
        { id: "accessed_value_words", label: "Accessed Value (Words)" },
        { id: "market_value", label: "Market Value" },
        { id: "market_value_words", label: "Market Value (Words)" },
        { id: "capital", label: "Capital" },
        { id: "capital_words", label: "Capital (Words)" },
        { id: "interest", label: "Interest" },
        { id: "interest_words", label: "Interest (Words)" },
        { id: "description", label: "Item Description" },
        { id: "reprint", label: "Re-Print" },
        { id: "total_weight_gram", label: "Total Weight (g)" },
        { id: "total_gold_weight_gram", label: "Total Gold Weight (g)" },
        { id: "total_accessed_value", label: "Total Accessed Value" },
        { id: "total_market_value", label: "Total Market Value" },
        { id: "carat_grouped", label: "Carat Grouped Summary" },
        { id: "carat_grouped_total", label: "Carat Grouped Total" }
    ]);

    const passbookHeadingFields: DesignerField[] = mergeFields([
        { id: "account_type_name", label: "Account Type Name" },
        { id: "account_number", label: "Account Number" },
        { id: "open_date", label: "Opening Date" },
        { id: "period", label: "Period" },
        { id: "period_minus_one", label: "Period - 1" },
        { id: "interest_rate", label: "Interest Rate" }
    ]);

    const passbookTransactionFields: DesignerField[] = [
        { id: "no", label: "Number (No)" },
        { id: "entered_date", label: "Entered Date" },
        { id: "code", label: "Code" },
        { id: "description", label: "Description" },
        { id: "debit", label: "Debit" },
        { id: "credit", label: "Credit" },
        { id: "balance", label: "Balance" },
        { id: "interest", label: "Interest" },
        { id: "penalty", label: "Penalty" },
        { id: "fund", label: "Fund" },
        { id: "passdue", label: "Pass Due" },
        { id: "insurance", label: "Insurance" },
        { id: "fee", label: "Fee" },
        { id: "cheque_num", label: "Cheque Number" },
        { id: "amount", label: "Amount" },
        { id: "entered_by", label: "Entered By" },
        { id: "entered_by_code", label: "Entered By Code" },
        { id: "updated_by", label: "Updated By" },
        { id: "updated_by_code", label: "Updated By Code" },
        { id: "reprint", label: "Re-Print" }
    ];

    const openingBalanceFields: DesignerField[] = [
        { id: "opening_tag", label: "Opening Tag" },
        { id: "balance", label: "Balance" },
        { id: "updated_date", label: "Date" },
    ];

    const transactionReceiptFields: DesignerField[] = [
        { id: "institute_name", label: "Institute Name" },
        { id: "branch_name", label: "Branch Name" },
        { id: "transaction_type", label: "Transaction Type" },
        { id: "customer_name", label: "Customer Name" },
        { id: "customer_number", label: "Customer Number" },
        { id: "customer_address", label: "Customer Address" },
        { id: "branch_address", label: "Branch Address" },
        { id: "transaction_number", label: "Transaction Number" },
        { id: "total_amount", label: "Total Amount" },
        { id: "total_amount_words", label: "Total Amount (Words)" },
        { id: "cheque_number", label: "Cheque Number" },
        { id: "date", label: "Date" },
        { id: "reprint", label: "Re-Print" },
        { id: "entered_by_id", label: "Entered By ID" },
        { id: "entered_by_name", label: "Entered By Name" },
        { id: "entered_by_code", label: "Entered By Code" },
        { id: "entered_by_ref", label: "Entered By Ref Number" },
        { id: "print_time", label: "Print Time" },
        { id: "transaction_type_id", label: "Transaction Type ID" },
        { id: "updated_by_name", label: "Updated By Name" },
        { id: "updated_by_code", label: "Updated By Code" },
        { id: "updated_by_id", label: "Updated By ID" },
        { id: "updated_by_ref", label: "Updated By Ref Number" },
        { id: "approved_by_name", label: "Approved By Name" },
        { id: "approved_by_code", label: "Approved By Code" },
        { id: "approved_by_id", label: "Approved By ID" },
        { id: "approved_by_ref", label: "Approved By Ref Number" },
        { id: "institute_address", label: "Institute Address" },
        { id: "institute_reg_num", label: "Institute Registration Number" },
        { id: "institute_phone", label: "Institute Phone" },
        { id: "institute_email", label: "Institute Email" },
        { id: "institute_reg_date", label: "Institute Registration Date" },
        { id: "branch_phone", label: "Branch Phone" },
        { id: "branch_email", label: "Branch Email" },
        { id: "interest_rate", label: "Interest Rate" },
        { id: "period", label: "Period" },
        { id: "period_minus_one", label: "Period - 1" },
        { id: "interest_rate_period", label: "Interest Rate & Period" },
        { id: "guarantor_members", label: "Guarantor Members" },
        { id: "invoice_date", label: "Invoice Date" },
        { id: "credit_period", label: "Credit Period" },
        { id: "note", label: "Note" },
        { id: "description", label: "Transaction Description" }
    ];

    // States
    const [printType, setPrintType] = useState<string>("1"); // 1 (Passbook), 2 (Passcard), 3 (Pawning Mandate), 4 (FD Certificate), Transaction Receipt
    const [passbookType, setPassbookType] = useState<string>("");
    const [category, setCategory] = useState<string>("FRONT_PAGE"); // FRONT_PAGE, HEADING
    const [txTypeId, setTxTypeId] = useState<string>("");
    const [subCategory, setSubCategory] = useState<string>("withdraw");
    // Sub-section state: all sub-categories stored inside the config JSON as nested keys
    const [fullConfig, setFullConfig] = useState<any>({});
    const [subSections, setSubSections] = useState<string[]>(["default"]);
    const [activeSubSection, setActiveSubSection] = useState<string>("default");

    const [categories, setCategories] = useState<PlAccountTypeCategory[]>([]);
    const [txTypes, setTxTypes] = useState<ItTransactionTypeResponse[]>([]);
    const [, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);

    const [backups, setBackups] = useState<string[]>([]);
    const [selectedBackup, setSelectedBackup] = useState<string>("");
    const [isBackingUp, setIsBackingUp] = useState<boolean>(false);
    const [isRestoring, setIsRestoring] = useState<boolean>(false);

    // Layout configuration values
    const [pageWidthCm, setPageWidthCm] = useState<string>("21.0");
    const [pageHeightCm, setPageHeightCm] = useState<string>("29.7");
    const [rawConfig, setRawConfig] = useState<any>({});

    // Field search
    const [fieldSearch, setFieldSearch] = useState<string>("");

    // Custom Fields added by user
    const [customFields, setCustomFields] = useState<DesignerField[]>([]);
    const [newFieldName, setNewFieldName] = useState<string>("");

    // Template image upload
    const [templateImage, setTemplateImage] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Selected element highlighting
    const [selectedElement, setSelectedElement] = useState<{ id: string; type: "value" | "label" } | null>(null);
    const [showGrid, setShowGrid] = useState<boolean>(false);
    const [alignmentLines, setAlignmentLines] = useState<{ x: number | null; y: number | null }>({ x: null, y: null });

    // Smooth drag refs — avoids state updates during pointermove for jank-free DnD
    interface SmoothDragState {
        active: boolean;
        element: HTMLElement | null;
        pxPerCm: number;
        startLeftCm: number;
        startTopCm: number;
        offsetX: number;
        offsetY: number;
        canvasRect: DOMRect | null;
        elWidth: number;
        elHeight: number;
        fieldId: string;
        type: "value" | "label";
        isTable: boolean;
        onCommit: (leftCm: string, topCm: string) => void;
    }
    const smoothDragRef = useRef<SmoothDragState>({
        active: false, element: null, pxPerCm: 1,
        startLeftCm: 0, startTopCm: 0, offsetX: 0, offsetY: 0,
        canvasRect: null, elWidth: 0, elHeight: 0,
        fieldId: '', type: "value", isTable: false,
        onCommit: () => { },
    });
    const rafRef = useRef<number | null>(null);

    useEffect(() => {
        setPageHeading("Visual Print Layout Designer");
        const loadInitialData = async () => {
            setLoading(true);
            try {
                const [cats, types, backupFiles] = await Promise.all([
                    getAccountTypeCategories(),
                    getAllTransactionTypes(),
                    printPolicyService.getBackups().catch(() => [] as string[])
                ]);
                setCategories(cats);
                setTxTypes(types);
                setBackups(backupFiles);
                if (backupFiles && backupFiles.length > 0) {
                    setSelectedBackup(backupFiles[0]);
                }
                if (cats.length > 0) setPassbookType(String(cats[0].id));
                if (types.length > 0) setTxTypeId(String(types[0].id));
            } catch (err: any) {
                toast.showError(err.message || "Failed to load designer data");
            } finally {
                setLoading(false);
            }
        };
        void loadInitialData();
    }, [setPageHeading]);

    // Active field options based on selections
    const availableFields = useMemo<DesignerField[]>(() => {
        let base: DesignerField[] = [];
        if (printType === "Transaction Receipt") {
            base = transactionReceiptFields;
        } else if (category === "PAWNING_MANDATE") {
            base = pawningMandateFields;
        } else if (category === "FD_CERTIFICATE") {
            base = fdCertificateFields;
        } else if (category === "HEADING") {
            base = passbookHeadingFields;
        } else if (category === "OPENING_BALANCE") {
            base = openingBalanceFields;
        } else if (category === "TRANSACTIONS") {
            base = passbookTransactionFields;
        } else {
            base = passbookFrontFields;
        }

        // Gather all copy fields present in rawConfig.required_fields or fields object
        const copyFields: DesignerField[] = [];

        const checkAndAddCopy = (fieldId: string) => {
            const match = fieldId.match(/^(.+)_copy_(\d+)$/);
            if (match) {
                const baseId = match[1];
                const copyNum = match[2];
                // Find base field label
                const baseField = base.find(f => f.id === baseId) || customFields.find(f => f.id === baseId);
                const baseLabel = baseField ? baseField.label : baseId.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
                if (!copyFields.some(f => f.id === fieldId)) {
                    copyFields.push({
                        id: fieldId,
                        label: `${baseLabel} (Copy ${copyNum})`
                    });
                }
            }
        };

        if (rawConfig) {
            // For Transaction Receipt
            if (rawConfig.fields) {
                Object.keys(rawConfig.fields).forEach(checkAndAddCopy);
            }
            // For others, look in required_fields
            if (Array.isArray(rawConfig.required_fields)) {
                rawConfig.required_fields.forEach(checkAndAddCopy);
            }
            // Check other keys for _top or _left suffix indicating copies
            Object.keys(rawConfig).forEach(key => {
                const match = key.match(/^(.+_copy_\d+)_(top|left)$/);
                if (match) {
                    checkAndAddCopy(match[1]);
                }
            });
        }

        return [...base, ...customFields, ...copyFields];
    }, [printType, category, customFields, rawConfig]);

    const loadingTxTypeRef = useRef<number | null>(null);

    // Fetch config
    const loadConfig = useCallback(async () => {
        const currentTxTypeId = txTypeId;
        if (printType === "Transaction Receipt" && !currentTxTypeId) return;
        setLoading(true);
        setRawConfig({});
        setTemplateImage(null);
        try {
            if (printType === "Transaction Receipt") {
                loadingTxTypeRef.current = Number(currentTxTypeId);
                const config = (await getTransactionTypePrintConfig(Number(currentTxTypeId))) as any;
                // Discard stale responses from previous type switches
                if (loadingTxTypeRef.current !== Number(currentTxTypeId) || Number(txTypeId) !== Number(currentTxTypeId)) return;
                                const isPayment = txTypes.find(t => String(t.id) === String(currentTxTypeId))?.type_name === "Payment";
                let baseTargetConfig = config || {};
                if (isPayment) {
                    if (config && (config.withdraw || config.payment)) {
                        baseTargetConfig = config[subCategory] || {};
                    } else {
                        baseTargetConfig = subCategory === "payment" ? (config || {}) : {};
                    }
                }
                
                let subConfig = baseTargetConfig;
                setFullConfig(config);
                
                // Check for sub_sections in Transaction Receipt config (nested under subCategory if Payment)
                const trSubSections = baseTargetConfig?.sub_sections;
                if (Array.isArray(trSubSections) && trSubSections.length > 0) {
                    const subKeys = trSubSections.map((obj: any) => Object.keys(obj)[0]).filter(Boolean);
                    setSubSections(["default", ...subKeys]);
                    const targetSub = ["default", ...subKeys].includes(activeSubSection) ? activeSubSection : "default";
                    if (targetSub !== activeSubSection) setActiveSubSection(targetSub);
                    if (targetSub === "default") {
                        const defaultConfig = { ...baseTargetConfig };
                        delete defaultConfig.sub_sections;
                        subConfig = defaultConfig;
                    } else {
                        const foundObj = trSubSections.find((obj: any) => Object.keys(obj)[0] === targetSub);
                        subConfig = foundObj ? foundObj[targetSub] : {};
                    }
                } else {
                    setSubSections(["default"]);
                    setActiveSubSection("default");
                }
                const merged = {
                    ...defaultPrintConfig,
                    ...(subConfig || {}),
                    page: {
                        ...defaultPrintConfig.page,
                        ...(subConfig?.page || {})
                    },
                    layout: {
                        ...defaultPrintConfig.layout,
                        ...(subConfig?.layout || {})
                    },
                    sections: {
                        ...defaultPrintConfig.sections,
                        ...(subConfig?.sections || {}),
                        table: {
                            ...defaultPrintConfig.sections.table,
                            ...(subConfig?.sections?.table || {})
                        }
                    },
                    fields: {
                        ...defaultPrintConfig.fields,
                        ...(subConfig?.fields || {})
                    }
                };
                setRawConfig(merged);
                setTemplateImage(merged.background_image || null);
                setPageWidthCm(merged.page?.width?.replace("cm", "") || "13.0");
                setPageHeightCm(merged.page?.height?.replace("cm", "") || "16.3");
            } else {
                const passbookPolicyId = Number(printType);

                const parsedPassbookType = (printType === "3") ? 1 : (printType === "4") ? 2 : Number(passbookType);
                if ((printType === "1" || printType === "2" || printType === "3" || printType === "4") && !parsedPassbookType) return;

                const policy = await printPolicyService.getPolicyConfig(parsedPassbookType!, category, passbookPolicyId);
                if (policy && policy.config) {
                    const parsed = JSON.parse(policy.config);

                    // Check for sub-categories stored under "sub_categories" key or "sub_sections" key
                    const subCats = parsed.sub_categories;
                    const subSectionsList = parsed.sub_sections;
                    const isNewSubCategory = printType !== "Transaction Receipt" && ["TRANSACTIONS", "FD_CERTIFICATE", "PAWNING_MANDATE", "HEADING", "OPENING_BALANCE"].includes(category);
                    let activeConfig: any;
                    if (category === "FRONT_PAGE" && subCats && typeof subCats === 'object' && !Array.isArray(subCats)) {
                        const subKeys = Object.keys(subCats);
                        setFullConfig(parsed);
                        setSubSections(["default", ...subKeys]);
                        const targetSub = ["default", ...subKeys].includes(activeSubSection) ? activeSubSection : "default";
                        if (targetSub !== activeSubSection) setActiveSubSection(targetSub);
                        if (targetSub === "default") {
                            const defaultConfig = { ...parsed };
                            delete defaultConfig.sub_categories;
                            activeConfig = defaultConfig;
                        } else {
                            activeConfig = subCats[targetSub] || {};
                        }
                    } else if (isNewSubCategory && Array.isArray(subSectionsList)) {
                        const subKeys = subSectionsList.map(obj => Object.keys(obj)[0]).filter(Boolean);
                        setFullConfig(parsed);
                        setSubSections(["default", ...subKeys]);
                        const targetSub = ["default", ...subKeys].includes(activeSubSection) ? activeSubSection : "default";
                        if (targetSub !== activeSubSection) setActiveSubSection(targetSub);
                        if (targetSub === "default") {
                            const defaultConfig = { ...parsed };
                            delete defaultConfig.sub_sections;
                            activeConfig = defaultConfig;
                        } else {
                            const foundObj = subSectionsList.find(obj => Object.keys(obj)[0] === targetSub);
                            activeConfig = foundObj ? foundObj[targetSub] : {};
                        }
                    } else {
                        // Flat config (no sub-categories or non-FRONT_PAGE)
                        setFullConfig({});
                        setSubSections(["default"]);
                        setActiveSubSection("default");
                        activeConfig = parsed;
                    }

                    const isA4 = category === "FD_CERTIFICATE" || category === "PAWNING_MANDATE";
                    const pageWidth = parseFloat(activeConfig.page_width?.replace("cm", "")) || (isA4 ? 21.0 : 13.0);
                    const marginLeft = parseFloat(activeConfig.margin_left?.replace("cm", "")) || 0.1;
                    const marginRight = parseFloat(activeConfig.margin_right?.replace("cm", "")) || 0.1;
                    const tableWidthCm = pageWidth - marginLeft - marginRight;

                    // Convert any percentage column widths to centimeters
                    Object.keys(activeConfig).forEach(key => {
                        const val = activeConfig[key];
                        if (val && typeof val === 'object' && val.column_width && typeof val.column_width === 'string' && val.column_width.includes('%')) {
                            const pct = parseFloat(val.column_width) || 10;
                            val.column_width = `${((tableWidthCm * pct) / 100).toFixed(2)}cm`;
                        }
                    });

                    setRawConfig(activeConfig);
                    setTemplateImage(activeConfig.background_image || null);
                    setPageWidthCm(activeConfig.page_width?.replace("cm", "") || (isA4 ? "21.0" : "13.0"));
                    setPageHeightCm(activeConfig.page_height?.replace("cm", "") || (isA4 ? "29.7" : "16.3"));

                    // Restore custom fields from required_fields if they aren't in defaults
                    const standardIds = new Set(availableFields.map(f => f.id));
                    const restoredCustoms: DesignerField[] = [];
                    if (activeConfig.required_fields) {
                        activeConfig.required_fields.forEach((fieldId: string) => {
                            const cleanId = fieldId.replace(/_copy_\d+$/, "");
                            if (!standardIds.has(cleanId) && !cleanId.endsWith("_label") && !restoredCustoms.some(f => f.id === cleanId)) {
                                restoredCustoms.push({
                                    id: cleanId,
                                    label: cleanId.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())
                                });
                            }
                        });
                    }
                    if (restoredCustoms.length > 0) {
                        setCustomFields(prev => {
                            const merged = [...prev];
                            restoredCustoms.forEach(rc => {
                                if (!merged.some(f => f.id === rc.id)) merged.push(rc);
                            });
                            return merged;
                        });
                    }
                }
            }
        } catch (err: any) {
            toast.showError(err.message || "Failed to load configurations");
        } finally {
            setLoading(false);
        }
    }, [printType, passbookType, category, txTypeId, availableFields, subCategory, txTypes]);

    useEffect(() => {
        void loadConfig();
    }, [printType, passbookType, category, txTypeId, subCategory]);

    // Canvas uses exact cm dimensions (matching PassBookPrint exactly)
    const pageWidthNum = parseFloat(pageWidthCm) || 21.0;
    const pageHeightNum = parseFloat(pageHeightCm) || 29.7;
    // dpi is used only for converting stored cm coordinates to inline cm strings (identity)
    const dpi = 1; // 1 cm per cm — all positions are kept in cm units directly

    // Image upload handling
    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (event) => {
                const base64 = event.target?.result as string;
                setTemplateImage(base64);
                setRawConfig((prev: any) => ({
                    ...prev,
                    background_image: base64
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    // Safe parsing coordinate strings e.g. "2.0cm" or "2"
    const getCoordinateValue = (val: string | undefined): number => {
        if (!val) return 0;
        return parseFloat(val.replace("cm", "")) || 0;
    };

    const getRequiredFields = (): string[] => {
        if (rawConfig && Array.isArray(rawConfig.required_fields)) {
            return rawConfig.required_fields;
        }
        if (category === "OPENING_BALANCE") {
            return ["opening_tag", "balance"];
        }
        return ["no", "entered_date", "code", "description", "debit", "credit", "balance", "entered_by", "entered_by_code", "updated_by", "updated_by_code"];
    };

    const DEFAULT_COLUMN_WIDTHS: Record<string, string> = {
        no: '1.2cm',
        entered_date: '2.5cm',
        code: '1.2cm',
        description: '4.5cm',
        debit: '2.5cm',
        credit: '2.5cm',
        balance: '3.0cm',
        interest: '2.0cm',
        entered_by: '1.8cm',
        entered_by_code: '1.8cm',
        updated_by: '1.8cm',
        updated_by_code: '1.8cm',
        reprint: '2.0cm',
        opening_tag: '4.0cm'
    };

    const getNormalizedColumnWidth = (widthVal: string | undefined, fieldId: string, tableWidthCm: number): string => {
        if (!widthVal) {
            return DEFAULT_COLUMN_WIDTHS[fieldId] || '2.0cm';
        }
        if (widthVal.includes('%')) {
            const pct = parseFloat(widthVal) || 10;
            return `${((tableWidthCm * pct) / 100).toFixed(2)}cm`;
        }
        if (widthVal.includes('cm')) {
            return widthVal;
        }
        const num = parseFloat(widthVal);
        if (!isNaN(num)) {
            return `${num.toFixed(2)}cm`;
        }
        return DEFAULT_COLUMN_WIDTHS[fieldId] || '2.0cm';
    };

    const getFieldLeftCm = (fieldId: string): string => {
        if (rawConfig[`${fieldId}_left`]) {
            return rawConfig[`${fieldId}_left`];
        }
        let sum = 0;
        const required = getRequiredFields();
        for (const f of required) {
            if (f === fieldId) break;
            const fCfg = rawConfig[f] || {};
            const colW = fCfg.column_width || '';
            const wCm = parseFloat(colW.replace(/[^0-9.]/g, '')) || parseFloat(DEFAULT_COLUMN_WIDTHS[f]) || 2.0;
            sum += wCm;
        }
        return `${sum.toFixed(2)}cm`;
    };

    const marginTopCm = getCoordinateValue(rawConfig.margin_top || "2.0cm");
    const marginLeftCm = getCoordinateValue(rawConfig.margin_left || "0.1cm");
    const marginRightCm = getCoordinateValue(rawConfig.margin_right || "0.1cm");
    const lineHeightCm = getCoordinateValue(rawConfig.line_height || "0.4cm");
    const tableWidthCm = pageWidthNum - marginLeftCm - marginRightCm;
    const tableWidthPx = tableWidthCm; // alias used by Transaction Receipt table box

    // Position updates from visual dragging
    const updateCoordinates = (fieldId: string, type: "value" | "label", leftCm: string, topCm: string) => {
        setRawConfig((prev: any) => {
            const copy = { ...prev };
            if (printType === "Transaction Receipt") {
                if (!copy.fields) copy.fields = {};
                if (!copy.fields[fieldId]) copy.fields[fieldId] = { top: "0", left: "0", visible: true };

                if (type === "value") {
                    copy.fields[fieldId].left = leftCm.replace("cm", "");
                    copy.fields[fieldId].top = topCm.replace("cm", "");
                } else {
                    copy.fields[fieldId].label_left = leftCm.replace("cm", "");
                    copy.fields[fieldId].label_top = topCm.replace("cm", "");
                }
            } else {
                if (type === "value") {
                    copy[`${fieldId}_left`] = leftCm;
                    copy[`${fieldId}_top`] = topCm;
                } else {
                    copy[`${fieldId}_label_left`] = leftCm;
                    copy[`${fieldId}_label_top`] = topCm;
                }
            }
            return copy;
        });
    };

    interface VisualElementCoord {
        id: string;
        type: "value" | "label";
        leftPx: number;
        topPx: number;
    }

    const getVisibleElementsCoords = (excludeId?: string, excludeType?: string): VisualElementCoord[] => {
        const list: VisualElementCoord[] = [];

        if (printType === "Transaction Receipt" || category === "PAWNING_MANDATE") {
            const sections = rawConfig.sections || {};
            const tableKeys = ["table", ...Object.keys(sections).filter(k => k.startsWith("table_copy_"))];
            tableKeys.forEach(tKey => {
                const tConfig = sections[tKey];
                if (tConfig && tConfig.enabled !== false) {
                    const tableId = tKey === "table" ? "table_section" : `table_section_${tKey}`;
                    if (excludeId !== tableId) {
                        const tableTopCm = tConfig.top || "7.5cm";
                        const tableLeftCm = tConfig.left || "2cm";
                        const tableTopPx = getCoordinateValue(tableTopCm) * dpi;
                        const tableLeftPx = getCoordinateValue(tableLeftCm) * dpi;
                        list.push({ id: tableId, type: "value", leftPx: tableLeftPx, topPx: tableTopPx });
                    }
                }
            });
        }

        availableFields.forEach(field => {
            const valVisible = isFieldVisible(field.id, "value");
            const lblVisible = isFieldVisible(field.id, "label");

            let vL = "0cm", vT = "0cm", lL = "0cm", lT = "0cm";
            if (printType === "Transaction Receipt") {
                const f = rawConfig.fields?.[field.id];
                if (f) {
                    vL = (f.left || "0") + "cm";
                    vT = (f.top || "0") + "cm";
                    lL = (f.label_left || "0") + "cm";
                    lT = (f.label_top || "0") + "cm";
                }
            } else if (category === "TRANSACTIONS" || category === "OPENING_BALANCE") {
                vL = getFieldLeftCm(field.id);
                vT = `${marginTopCm}cm`;
                lL = "0cm";
                lT = "0cm";
            } else {
                vL = rawConfig[`${field.id}_left`] || "0cm";
                vT = rawConfig[`${field.id}_top`] || "0cm";
                lL = rawConfig[`${field.id}_label_left`] || "0cm";
                lT = rawConfig[`${field.id}_label_top`] || "0cm";
            }

            if (valVisible && !(excludeId === field.id && excludeType === "value")) {
                list.push({
                    id: field.id,
                    type: "value",
                    leftPx: getCoordinateValue(vL) * dpi,
                    topPx: getCoordinateValue(vT) * dpi
                });
            }
            if (lblVisible && !(excludeId === field.id && excludeType === "label")) {
                list.push({
                    id: field.id,
                    type: "label",
                    leftPx: getCoordinateValue(lL) * dpi,
                    topPx: getCoordinateValue(lT) * dpi
                });
            }
        });

        return list;
    };

    // Dragging Handler — uses CSS transform during drag (no state updates) for smooth DnD
    const handlePointerDown = (e: React.PointerEvent, fieldId: string, type: "value" | "label") => {
        e.preventDefault();
        setSelectedElement({ id: fieldId, type });

        const dragTarget = e.currentTarget as HTMLDivElement;
        const canvas = dragTarget.offsetParent as HTMLDivElement;
        if (!canvas) return;

        const canvasRect = canvas.getBoundingClientRect();
        const targetRect = dragTarget.getBoundingClientRect();

        // Compute how many screen pixels correspond to 1 cm using the live canvas size
        const pxPerCm = canvasRect.width / pageWidthNum;

        // Read starting position from config
        let startL = 0, startT = 0;
        if (printType === "Transaction Receipt") {
            const f = rawConfig.fields?.[fieldId];
            if (f) {
                if (type === "value") {
                    startL = parseFloat(f.left || "0");
                    startT = parseFloat(f.top || "0");
                } else {
                    startL = parseFloat(f.label_left || "0");
                    startT = parseFloat(f.label_top || "0");
                }
            }
        } else {
            if (type === "value") {
                startL = getCoordinateValue(rawConfig[`${fieldId}_left`] || "0cm");
                startT = getCoordinateValue(rawConfig[`${fieldId}_top`] || "0cm");
            } else {
                startL = getCoordinateValue(rawConfig[`${fieldId}_label_left`] || "0cm");
                startT = getCoordinateValue(rawConfig[`${fieldId}_label_top`] || "0cm");
            }
        }

        const onCommit = (leftCm: string, topCm: string) => {
            updateCoordinates(fieldId, type, leftCm, topCm);
        };

        smoothDragRef.current = {
            active: true,
            element: dragTarget,
            pxPerCm,
            startLeftCm: startL,
            startTopCm: startT,
            offsetX: e.clientX - targetRect.left,
            offsetY: e.clientY - targetRect.top,
            canvasRect,
            elWidth: targetRect.width,
            elHeight: targetRect.height,
            fieldId,
            type,
            isTable: false,
            onCommit,
        };

        dragTarget.style.willChange = "transform";

        const handlePointerMove = (moveEvent: PointerEvent) => {
            if (rafRef.current !== null) return;
            rafRef.current = requestAnimationFrame(() => {
                rafRef.current = null;
                const d = smoothDragRef.current;
                if (!d.active || !d.element) return;

                let leftPx = moveEvent.clientX - (d.canvasRect?.left || 0) - d.offsetX;
                let topPx = moveEvent.clientY - (d.canvasRect?.top || 0) - d.offsetY;

                const cw = d.canvasRect?.width || 0;
                const ch = d.canvasRect?.height || 0;

                leftPx = Math.max(0, Math.min(leftPx, cw - d.elWidth));
                topPx = Math.max(0, Math.min(topPx, ch - d.elHeight));

                let leftCmVal = leftPx / d.pxPerCm;
                let topCmVal = (category === "TRANSACTIONS" || category === "OPENING_BALANCE") ? marginTopCm : topPx / d.pxPerCm;

                // Snapping threshold (0.15 cm)
                const threshold = 0.15;
                const otherCoords = getVisibleElementsCoords(fieldId, type);

                let snapX: number | null = null;
                let snapY: number | null = null;

                for (const other of otherCoords) {
                    if (Math.abs(leftCmVal - other.leftPx) < threshold) {
                        leftCmVal = other.leftPx;
                        snapX = other.leftPx * d.pxPerCm;
                    }
                    if (Math.abs(topCmVal - other.topPx) < threshold) {
                        topCmVal = other.topPx;
                        snapY = other.topPx * d.pxPerCm;
                    }
                }

                // Apply CSS transform for visual movement (no state update)
                const dxPx = (leftCmVal - d.startLeftCm) * d.pxPerCm;
                const dyPx = (topCmVal - d.startTopCm) * d.pxPerCm;
                d.element!.style.transform = `translate(${dxPx.toFixed(1)}px, ${dyPx.toFixed(1)}px)`;

                setAlignmentLines({ x: snapX, y: snapY });
            });
        };

        const handlePointerUp = () => {
            document.removeEventListener("pointermove", handlePointerMove);
            document.removeEventListener("pointerup", handlePointerUp);
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }

            const d = smoothDragRef.current;
            if (d.active && d.element) {
                let dxCm = 0, dyCm = 0;
                const transform = d.element.style.transform;
                if (transform) {
                    const match = transform.match(/translate\(([\d.-]+)px,\s*([\d.-]+)px\)/);
                    if (match) {
                        dxCm = parseFloat(match[1]) / d.pxPerCm;
                        dyCm = parseFloat(match[2]) / d.pxPerCm;
                    }
                }
                const finalLeftCm = (d.startLeftCm + dxCm).toFixed(2) + "cm";
                let finalTopCm = (d.startTopCm + dyCm).toFixed(2) + "cm";
                if (category === "TRANSACTIONS" || category === "OPENING_BALANCE") {
                    finalTopCm = (marginTopCm).toFixed(2) + "cm";
                }
                d.onCommit(finalLeftCm, finalTopCm);

                // Reset element visual state
                d.element.style.transform = "";
                d.element.style.willChange = "";
            }

            smoothDragRef.current.active = false;
            smoothDragRef.current.element = null;
            setAlignmentLines({ x: null, y: null });
        };

        document.addEventListener("pointermove", handlePointerMove);
        document.addEventListener("pointerup", handlePointerUp);
    };

    // Checkbox togglers
    const isFieldVisible = (fieldId: string, type: "value" | "label"): boolean => {
        if (printType === "Transaction Receipt") {
            const f = rawConfig.fields?.[fieldId];
            if (!f) return false;
            return type === "value" ? Boolean(f.visible) : Boolean(f.show_label);
        } else if (category === "TRANSACTIONS" || category === "OPENING_BALANCE") {
            if (type === "label") return false;
            return Boolean(rawConfig.required_fields?.includes(fieldId));
        } else {
            const req = rawConfig.required_fields || [];
            if (type === "value") {
                return req.includes(fieldId);
            } else {
                return Boolean(rawConfig[`${fieldId}_show_label`]);
            }
        }
    };

    const toggleFieldVisibility = (fieldId: string, type: "value" | "label") => {
        setRawConfig((prev: any) => {
            const copy = { ...prev };
            if (printType === "Transaction Receipt") {
                if (!copy.fields) copy.fields = {};
                if (!copy.fields[fieldId]) copy.fields[fieldId] = { top: "0", left: "0", visible: false };

                if (type === "value") {
                    copy.fields[fieldId].visible = !copy.fields[fieldId].visible;
                } else {
                    copy.fields[fieldId].show_label = !copy.fields[fieldId].show_label;
                    if (copy.fields[fieldId].show_label && !copy.fields[fieldId].label_left) {
                        copy.fields[fieldId].label_left = "0.0";
                        copy.fields[fieldId].label_top = "0.0";
                    }
                }
            } else if (category === "TRANSACTIONS" || category === "OPENING_BALANCE") {
                if (!copy.required_fields) copy.required_fields = [];
                if (copy.required_fields.includes(fieldId)) {
                    copy.required_fields = copy.required_fields.filter((id: string) => id !== fieldId);
                } else {
                    copy.required_fields.push(fieldId);
                    if (!copy[fieldId]) {
                        copy[fieldId] = { column_width: "2.0cm", bold: false };
                    }
                }
            } else {
                if (!copy.required_fields) copy.required_fields = [];
                if (type === "value") {
                    if (copy.required_fields.includes(fieldId)) {
                        copy.required_fields = copy.required_fields.filter((id: string) => id !== fieldId);
                        delete copy[`${fieldId}_left`];
                        delete copy[`${fieldId}_top`];
                    } else {
                        copy.required_fields.push(fieldId);
                        copy[`${fieldId}_left`] = "1.0cm";
                        copy[`${fieldId}_top`] = "1.0cm";
                    }
                } else {
                    copy[`${fieldId}_show_label`] = !copy[`${fieldId}_show_label`];
                    if (copy[`${fieldId}_show_label`]) {
                        copy[`${fieldId}_label_left`] = "1.0cm";
                        copy[`${fieldId}_label_top`] = "1.0cm";
                    } else {
                        delete copy[`${fieldId}_label_left`];
                        delete copy[`${fieldId}_label_top`];
                    }
                }
            }
            return copy;
        });
    };


    // Custom labels translation management
    const getCustomLabelValue = (fieldId: string, lang: "en" | "si" | "ta"): string => {
        if (printType === "Transaction Receipt") {
            return rawConfig.fields?.[fieldId]?.[`label_${lang}`] || "";
        } else {
            return rawConfig[`${fieldId}_label_${lang}`] || "";
        }
    };

    const handleCustomLabelChange = (fieldId: string, lang: "en" | "si" | "ta", value: string) => {
        setRawConfig((prev: any) => {
            const copy = { ...prev };
            if (printType === "Transaction Receipt") {
                if (!copy.fields) copy.fields = {};
                if (!copy.fields[fieldId]) copy.fields[fieldId] = { top: "0", left: "0", visible: true };
                copy.fields[fieldId][`label_${lang}`] = value;
            } else {
                copy[`${fieldId}_label_${lang}`] = value;
            }
            return copy;
        });
    };

    // Table Section Configuration helper & drag handler
    const updateTableCoordinates = (leftCm: string, topCm: string, tableId: string = "table_section") => {
        const tKey = getTableKey(tableId);
        setRawConfig((prev: any) => {
            const copy = { ...prev };
            if (!copy.sections) copy.sections = {};
            if (!copy.sections[tKey]) {
                copy.sections[tKey] = { top: "7.5cm", left: "2cm", width: "15cm", row_height: "0.8cm" };
            }
            copy.sections[tKey].left = leftCm;
            copy.sections[tKey].top = topCm;
            return copy;
        });
    };

    const handleTablePointerDown = (e: React.PointerEvent, tableId: string = "table_section") => {
        e.preventDefault();
        setSelectedElement({ id: tableId, type: "value" });

        const dragTarget = e.currentTarget as HTMLDivElement;
        const canvas = dragTarget.offsetParent as HTMLDivElement;
        if (!canvas) return;

        const canvasRect = canvas.getBoundingClientRect();
        const targetRect = dragTarget.getBoundingClientRect();

        // Compute screen pixels per cm from live canvas size
        const pxPerCm = canvasRect.width / pageWidthNum;

        const tKey = tableId === "table_section" ? "table" : tableId.replace("table_section_", "");
        const tableConfig = rawConfig.sections?.[tKey] || {};
        const startL = getCoordinateValue(tableConfig.left || "2cm");
        const startT = getCoordinateValue(tableConfig.top || "7.5cm");

        const onCommit = (leftCm: string, topCm: string) => {
            updateTableCoordinates(leftCm, topCm, tableId);
        };

        smoothDragRef.current = {
            active: true,
            element: dragTarget,
            pxPerCm,
            startLeftCm: startL,
            startTopCm: startT,
            offsetX: e.clientX - targetRect.left,
            offsetY: e.clientY - targetRect.top,
            canvasRect,
            elWidth: targetRect.width,
            elHeight: targetRect.height,
            fieldId: tableId,
            type: "value",
            isTable: true,
            onCommit,
        };

        dragTarget.style.willChange = "transform";

        const handlePointerMove = (moveEvent: PointerEvent) => {
            if (rafRef.current !== null) return;
            rafRef.current = requestAnimationFrame(() => {
                rafRef.current = null;
                const d = smoothDragRef.current;
                if (!d.active || !d.element) return;

                let leftPx = moveEvent.clientX - (d.canvasRect?.left || 0) - d.offsetX;
                let topPx = moveEvent.clientY - (d.canvasRect?.top || 0) - d.offsetY;

                const cw = d.canvasRect?.width || 0;
                const ch = d.canvasRect?.height || 0;

                leftPx = Math.max(0, Math.min(leftPx, cw - d.elWidth));
                topPx = Math.max(0, Math.min(topPx, ch - d.elHeight));

                const threshold = 0.15; // cm
                const otherCoords = getVisibleElementsCoords(tableId, "value");

                let leftCmVal = leftPx / d.pxPerCm;
                let topCmVal = topPx / d.pxPerCm;

                let snapX: number | null = null;
                let snapY: number | null = null;

                for (const other of otherCoords) {
                    if (Math.abs(leftCmVal - other.leftPx) < threshold) {
                        leftCmVal = other.leftPx;
                        snapX = other.leftPx * d.pxPerCm;
                    }
                    if (Math.abs(topCmVal - other.topPx) < threshold) {
                        topCmVal = other.topPx;
                        snapY = other.topPx * d.pxPerCm;
                    }
                }

                // Apply CSS transform (no state update)
                const dxPx = (leftCmVal - d.startLeftCm) * d.pxPerCm;
                const dyPx = (topCmVal - d.startTopCm) * d.pxPerCm;
                d.element!.style.transform = `translate(${dxPx.toFixed(1)}px, ${dyPx.toFixed(1)}px)`;

                setAlignmentLines({ x: snapX, y: snapY });
            });
        };

        const handlePointerUp = () => {
            document.removeEventListener("pointermove", handlePointerMove);
            document.removeEventListener("pointerup", handlePointerUp);
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }

            const d = smoothDragRef.current;
            if (d.active && d.element) {
                let dxCm = 0, dyCm = 0;
                const transform = d.element.style.transform;
                if (transform) {
                    const match = transform.match(/translate\(([\d.-]+)px,\s*([\d.-]+)px\)/);
                    if (match) {
                        dxCm = parseFloat(match[1]) / d.pxPerCm;
                        dyCm = parseFloat(match[2]) / d.pxPerCm;
                    }
                }
                const finalLeftCm = (d.startLeftCm + dxCm).toFixed(2) + "cm";
                const finalTopCm = (d.startTopCm + dyCm).toFixed(2) + "cm";
                d.onCommit(finalLeftCm, finalTopCm);

                d.element.style.transform = "";
                d.element.style.willChange = "";
            }

            smoothDragRef.current.active = false;
            smoothDragRef.current.element = null;
            setAlignmentLines({ x: null, y: null });
        };

        document.addEventListener("pointermove", handlePointerMove);
        document.addEventListener("pointerup", handlePointerUp);
    };

    const getTableKey = (selectedId: string): string => {
        if (selectedId === "table_section") return "table";
        return selectedId.replace("table_section_", "");
    };

    const getColWidthPx = (col: string, tableId: string = "table_section"): string => {
        // Canvas is cm-based — return cm or % values for column widths
        const tKey = getTableKey(tableId);
        const tableConfig = rawConfig.sections?.[tKey] || {};
        const tableAutoFitLocal = (printType === "Transaction Receipt" || category === "PAWNING_MANDATE") ? false : (tableConfig.auto_fit !== 'false');
        const tableWidthUsed = tableAutoFitLocal ? tableWidthCm : getCoordinateValue(tableConfig.width || "15cm");
        const colWidthVal = tableConfig[`${col}_width`] || "";

        if (colWidthVal.includes("cm")) {
            return colWidthVal;
        } else if (colWidthVal.includes("%")) {
            const pct = parseFloat(colWidthVal) / 100;
            return `${(tableWidthUsed * pct).toFixed(2)}cm`;
        } else {
            let fallbackPct = 25;
            if (col === "account_number") fallbackPct = 30;
            else if (col === "account_name") fallbackPct = 40;
            else if (col === "description") fallbackPct = 40;
            else if (col === "balance") fallbackPct = 25;
            else if (col === "name") fallbackPct = 25;
            else if (col === "quantity" || col === "carat" || col === "weight_gram" || col === "gold_weight_gram") fallbackPct = 10;
            else if (col === "accessed_value" || col === "market_value" || col === "capital") fallbackPct = 15;
            return `${(tableWidthUsed * fallbackPct / 100).toFixed(2)}cm`;
        }
    };

    const getTableConfigValue = (key: string, tableId?: string): string => {
        const tId = tableId || (selectedElement && (selectedElement.id === "table_section" || selectedElement.id.startsWith("table_section_")) ? selectedElement.id : "table_section");
        const tKey = getTableKey(tId);
        return rawConfig.sections?.[tKey]?.[key] || "";
    };

    const handleTableConfigChange = (key: string, value: string, tableId?: string) => {
        const tId = tableId || (selectedElement && (selectedElement.id === "table_section" || selectedElement.id.startsWith("table_section_")) ? selectedElement.id : "table_section");
        const tKey = getTableKey(tId);
        setRawConfig((prev: any) => {
            const copy = { ...prev };
            if (!copy.sections) copy.sections = {};
            if (!copy.sections[tKey]) {
                copy.sections[tKey] = { top: "7.5cm", left: "2cm", width: "15cm", row_height: "0.8cm" };
            }
            copy.sections[tKey][key] = value;
            return copy;
        });
    };

    const handleCopyTableSection = () => {
        let nextCopyNum = 1;
        const sections = rawConfig.sections || {};
        while (sections[`table_copy_${nextCopyNum}`]) {
            nextCopyNum++;
        }
        const newTableKey = `table_copy_${nextCopyNum}`;
        const currentTableId = selectedElement && (selectedElement.id === "table_section" || selectedElement.id.startsWith("table_section_")) ? selectedElement.id : "table_section";
        const currentTableKey = getTableKey(currentTableId);
        const originalTableCfg = sections[currentTableKey] || { top: "7.5cm", left: "2cm", width: "15cm", row_height: "0.8cm" };

        setRawConfig((prev: any) => {
            const copy = { ...prev };
            if (!copy.sections) copy.sections = {};

            const origTop = parseFloat(String(originalTableCfg.top || "7.5cm").replace("cm", ""));
            const newTop = (origTop + 1.0).toFixed(2) + "cm";

            copy.sections[newTableKey] = {
                ...originalTableCfg,
                top: newTop,
                left: originalTableCfg.left || "2cm"
            };
            return copy;
        });

        const newTableId = `table_section_${newTableKey}`;
        setSelectedElement({ id: newTableId, type: "value" });
        toast.showSuccess(`Duplicated table section as Copy ${nextCopyNum}`);
    };

    const handleDeleteTableSection = (tableId: string) => {
        if (!tableId.startsWith("table_section_")) return;
        const tableKey = getTableKey(tableId);

        setRawConfig((prev: any) => {
            const copy = { ...prev };
            if (copy.sections && copy.sections[tableKey]) {
                delete copy.sections[tableKey];
            }
            return copy;
        });

        setSelectedElement({ id: "table_section", type: "value" });
        toast.showSuccess("Deleted table section copy");
    };

    const handleMoveColumn = (colId: string, direction: -1 | 1, tableId?: string) => {
        const tId = tableId || (selectedElement && (selectedElement.id === "table_section" || selectedElement.id.startsWith("table_section_")) ? selectedElement.id : "table_section");
        const tKey = getTableKey(tId);
        setRawConfig((prev: any) => {
            const copy = { ...prev };
            if (printType === "Transaction Receipt" || category === "PAWNING_MANDATE") {
                if (!copy.sections) copy.sections = {};
                if (!copy.sections[tKey]) {
                    copy.sections[tKey] = { top: "7.5cm", left: "2cm", width: "15cm", row_height: "0.8cm" };
                }
                const defaultCols = getDefaultTableColumns(printType, category);
                const activeCols = [...(copy.sections[tKey].columns || defaultCols)];
                const index = activeCols.indexOf(colId);
                if (index === -1) return prev;
                const newIndex = index + direction;
                if (newIndex < 0 || newIndex >= activeCols.length) return prev;

                const temp = activeCols[index];
                activeCols[index] = activeCols[newIndex];
                activeCols[newIndex] = temp;

                copy.sections[tKey].columns = activeCols;
            } else {
                const required = [...(copy.required_fields || ["no", "entered_date", "code", "description", "debit", "credit", "balance", "entered_by", "entered_by_code", "updated_by", "updated_by_code"])];
                const index = required.indexOf(colId);
                if (index === -1) return prev;
                const newIndex = index + direction;
                if (newIndex < 0 || newIndex >= required.length) return prev;

                const temp = required[index];
                required[index] = required[newIndex];
                required[newIndex] = temp;

                copy.required_fields = required;
            }
            return copy;
        });
    };

    const getFieldStyle = (fieldId: string, styleKey: "bold" | "italic" | "font_size" | "translate" | "label_translate" | "hide_percent" | "hide_months"): any => {
        if (printType === "Transaction Receipt") {
            const f = rawConfig.fields?.[fieldId];
            if (styleKey === "bold" || styleKey === "italic" || styleKey === "hide_percent" || styleKey === "hide_months") {
                return f ? Boolean(f[styleKey]) : false;
            }
            if (styleKey === "translate") {
                return f && f.translate !== undefined ? Boolean(f.translate) : true;
            }
            if (styleKey === "label_translate") {
                return f && f.label_translate !== undefined ? Boolean(f.label_translate) : true;
            }
            return f?.[styleKey] || "";
        } else {
            const f = rawConfig[fieldId];
            if (styleKey === "bold" || styleKey === "italic" || styleKey === "hide_percent" || styleKey === "hide_months") {
                return f ? Boolean(f[styleKey]) : false;
            }
            if (styleKey === "translate") {
                return f && f.translate !== undefined ? Boolean(f.translate) : true;
            }
            if (styleKey === "label_translate") {
                return f && f.label_translate !== undefined ? Boolean(f.label_translate) : true;
            }
            return f?.[styleKey] || "";
        }
    };

    const getFieldDisplayValue = (fieldId: string) => {
        const dateFields = ["institute_reg_date", "opening_date", "date_of_birth", "member_date", "last_transaction_date", "credit_limit_date", "entered_date", "date", "open_date", "first_installment_date", "last_installment_date"];
        const cleanFieldId = fieldId.replace(/_copy_\d+$/, "");
        if (cleanFieldId === "interest_rate_period") {
            return "Per 12 Months rate 12.5%";
        }
        if (cleanFieldId === "print_time") {
            const fmt = rawConfig.time_format || "HH:mm:ss";
            return `${fieldId} (${fmt === 'HH:mm:ss' ? '14:30:00' : fmt === 'HH:mm' ? '14:30' : fmt === 'hh:mm:ss a' ? '02:30:00 PM' : '02:30 PM'})`;
        }
        if (dateFields.includes(cleanFieldId)) {
            const fmt = rawConfig.date_format || "yyyy-MM-dd";
            const year = "2026";
            const yy = "26";
            const MM = "06";
            const M = "6";
            const dd = "17";
            const d = "17";

            const lowerFormat = fmt.toLowerCase();
            let result = lowerFormat;

            if (lowerFormat.includes("yyyy")) {
                result = result.replace("yyyy", year);
            } else if (lowerFormat.includes("yy")) {
                result = result.replace("yy", yy);
            }

            if (result.includes("mm")) {
                result = result.replace("mm", MM);
            } else if (result.includes("m")) {
                result = result.replace("m", M);
            }

            if (result.includes("dd")) {
                result = result.replace("dd", dd);
            } else if (result.includes("d")) {
                result = result.replace("d", d);
            }

            return `${fieldId} (${result})`;
        }
        return fieldId;
    };

    const handleFieldStyleChange = (fieldId: string, styleKey: "bold" | "italic" | "font_size" | "translate" | "label_translate" | "hide_percent" | "hide_months", value: any) => {
        setRawConfig((prev: any) => {
            const copy = { ...prev };
            if (printType === "Transaction Receipt") {
                if (!copy.fields) copy.fields = {};
                if (!copy.fields[fieldId]) copy.fields[fieldId] = { top: "0", left: "0", visible: true };
                copy.fields[fieldId][styleKey] = value;
            } else {
                if (!copy[fieldId]) copy[fieldId] = {};
                copy[fieldId][styleKey] = value;
            }
            return copy;
        });
    };

    const getSelectedCoordinate = (coord: "top" | "left"): string => {
        if (!selectedElement) return "";
        const fieldId = selectedElement.id;
        const type = selectedElement.type;

        if (fieldId === "table_section") {
            return (rawConfig.sections?.table?.[coord] || "").replace("cm", "");
        }

        if (printType === "Transaction Receipt") {
            const f = rawConfig.fields?.[fieldId];
            if (!f) return "";
            const val = type === "value" ? f[coord] : f[`label_${coord}`];
            return String(val || "").replace("cm", "");
        } else {
            const val = type === "value" ? rawConfig[`${fieldId}_${coord}`] : rawConfig[`${fieldId}_label_${coord}`];
            return String(val || "").replace("cm", "");
        }
    };

    const handleSelectedCoordinateChange = (coord: "top" | "left", value: string) => {
        if (!selectedElement) return;
        const fieldId = selectedElement.id;
        const type = selectedElement.type;

        if (fieldId === "table_section") {
            const cleanVal = value.trim() === "" ? "" : value.endsWith("cm") ? value : value + "cm";
            handleTableConfigChange(coord, cleanVal);
            return;
        }

        const cleanVal = value.trim() === "" ? "" : value.endsWith("cm") ? value : value + "cm";
        const unitlessVal = value.replace("cm", "");

        setRawConfig((prev: any) => {
            const copy = { ...prev };
            if (printType === "Transaction Receipt") {
                if (!copy.fields) copy.fields = {};
                if (!copy.fields[fieldId]) copy.fields[fieldId] = { top: "0", left: "0", visible: true };

                if (type === "value") {
                    copy.fields[fieldId][coord] = unitlessVal;
                } else {
                    copy.fields[fieldId][`label_${coord}`] = unitlessVal;
                }
            } else {
                if (type === "value") {
                    copy[`${fieldId}_${coord}`] = cleanVal;
                } else {
                    copy[`${fieldId}_label_${coord}`] = cleanVal;
                }
            }
            return copy;
        });
    };


    const handleColResizeMouseDown = (e: React.MouseEvent, fieldId: string) => {
        e.stopPropagation();
        e.preventDefault();

        const startX = e.clientX;
        // Get the canvas element to compute pxPerCm
        const canvas = (e.currentTarget as HTMLElement).closest('[style*="cm"]') as HTMLElement || document.body;
        const canvasRect = canvas.getBoundingClientRect();
        const pxPerCm = canvasRect.width / pageWidthNum;

        const initialWidthStr = rawConfig[fieldId]?.column_width || "2.0cm";

        let startWidthCm = 2.0;
        if (initialWidthStr.includes("cm")) {
            startWidthCm = parseFloat(initialWidthStr.replace("cm", "")) || 2.0;
        } else if (initialWidthStr.includes("%")) {
            const pct = parseFloat(initialWidthStr.replace("%", "")) / 100;
            startWidthCm = tableWidthCm * pct;
        } else if (!isNaN(parseFloat(initialWidthStr))) {
            startWidthCm = parseFloat(initialWidthStr);
        }

        const handleMouseMove = (moveEvent: MouseEvent) => {
            if (rafRef.current !== null) return;
            rafRef.current = requestAnimationFrame(() => {
                rafRef.current = null;
                const deltaX = moveEvent.clientX - startX;
                const deltaCm = deltaX / pxPerCm;
                const newWidthCm = Math.max(0.3, startWidthCm + deltaCm);
                const newWidthStr = `${newWidthCm.toFixed(2)}cm`;

                setRawConfig((prev: any) => {
                    const copy = { ...prev };
                    if (!copy[fieldId]) copy[fieldId] = {};
                    copy[fieldId].column_width = newWidthStr;
                    return copy;
                });
            });
        };

        const handleMouseUp = () => {
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };

        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
    };

    const handleTransactionColResizePointerDown = (
        e: React.PointerEvent,
        col: string,
        tableId: string = "table_section"
    ) => {
        e.stopPropagation();
        e.preventDefault();

        const tKey = getTableKey(tableId);

        try {
            (e.currentTarget as any).setPointerCapture?.(e.pointerId);
        } catch { }

        const startX = e.clientX;
        const handle = e.currentTarget as HTMLElement;
        const canvas = handle.closest('[style*="cm"]') as HTMLElement || document.body;
        const canvasRect = canvas.getBoundingClientRect();
        const pxPerCm = canvasRect.width / pageWidthNum;

        const tableWidthUsed = getCoordinateValue(rawConfig.sections?.[tKey]?.width || "15cm");
        const initialWidthStr = rawConfig.sections?.[tKey]?.[`${col}_width`] || "";

        let startWidthCm = 0;
        if (initialWidthStr.includes("cm")) {
            startWidthCm = parseFloat(initialWidthStr.replace("cm", ""));
        } else if (initialWidthStr.includes("%")) {
            const pct = parseFloat(initialWidthStr.replace("%", "")) / 100;
            startWidthCm = tableWidthUsed * pct;
        } else {
            let fallbackPct = 25;
            if (col === "account_number") fallbackPct = 30;
            else if (col === "account_name") fallbackPct = 40;
            else if (col === "description") fallbackPct = 40;
            else if (col === "balance") fallbackPct = 25;
            else if (col === "name") fallbackPct = 25;
            else if (col === "quantity" || col === "carat" || col === "weight_gram" || col === "gold_weight_gram") fallbackPct = 10;
            else if (col === "accessed_value" || col === "market_value" || col === "capital") fallbackPct = 15;
            startWidthCm = tableWidthUsed * (fallbackPct / 100);
        }
        if (isNaN(startWidthCm) || startWidthCm <= 0) {
            startWidthCm = 2.0;
        }

        const onPointerMove = (moveEvent: PointerEvent) => {
            if (rafRef.current !== null) return;
            rafRef.current = requestAnimationFrame(() => {
                rafRef.current = null;
                const deltaX = moveEvent.clientX - startX;
                const deltaCm = deltaX / pxPerCm;
                const newWidthCm = Math.max(0.3, startWidthCm + deltaCm);
                const newWidthStr = `${newWidthCm.toFixed(2)}cm`;

                setRawConfig((prev: any) => {
                    const copy = { ...prev };
                    if (!copy.sections) copy.sections = {};
                    if (!copy.sections[tKey]) {
                        copy.sections[tKey] = { top: "7.5cm", left: "2cm", width: "15cm", row_height: "0.8cm" };
                    }
                    copy.sections[tKey][`${col}_width`] = newWidthStr;
                    return copy;
                });
            });
        };

        const onPointerUp = (upEvent: PointerEvent) => {
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
            try {
                (e.currentTarget as any).releasePointerCapture?.(upEvent.pointerId);
            } catch { }
            document.removeEventListener("pointermove", onPointerMove);
            document.removeEventListener("pointerup", onPointerUp);
        };

        document.addEventListener("pointermove", onPointerMove);
        document.addEventListener("pointerup", onPointerUp);
    };

    // Table width resize handlers (drag right edge) - use pointer events so parent pointer handlers don't interfere
    const handleTableResizePointerDown = (e: React.PointerEvent, tableId: string = "table_section") => {
        e.stopPropagation();
        e.preventDefault();

        // capture the pointer on the handle so move events are reliably delivered
        try {
            // currentTarget is the handle div
            (e.currentTarget as any).setPointerCapture?.(e.pointerId);
        } catch {
            // ignore if not supported
        }

        const tKey = getTableKey(tableId);
        const startX = e.clientX;
        // Get pxPerCm from the canvas live render
        const handle = e.currentTarget as HTMLElement;
        const tableEl = handle.closest('[style*="position: absolute"]') as HTMLElement;
        const canvasEl = tableEl?.offsetParent as HTMLElement;
        const canvasRenderedWidth = canvasEl?.getBoundingClientRect().width || (pageWidthNum * 37.8);
        const pxPerCm = canvasRenderedWidth / pageWidthNum;

        const initialWidth = rawConfig.sections?.[tKey]?.width || "15cm";
        const startWidthCm = getCoordinateValue(initialWidth);

        const onPointerMove = (moveEvent: PointerEvent) => {
            if (rafRef.current !== null) return;
            rafRef.current = requestAnimationFrame(() => {
                rafRef.current = null;
                const delta = moveEvent.clientX - startX;
                const deltaCm = delta / pxPerCm;
                const newWidthCm = Math.max(0.5, startWidthCm + deltaCm);
                handleTableConfigChange('width', `${newWidthCm.toFixed(2)}cm`, tableId);
            });
        };

        const onPointerUp = (upEvent: PointerEvent) => {
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
            try {
                (e.currentTarget as any).releasePointerCapture?.((upEvent as any).pointerId);
            } catch { }
            document.removeEventListener('pointermove', onPointerMove as any);
            document.removeEventListener('pointerup', onPointerUp as any);
        };

        document.addEventListener('pointermove', onPointerMove as any);
        document.addEventListener('pointerup', onPointerUp as any);
    };

    // Add new custom field
    const handleAddCustomField = () => {
        if (!newFieldName.trim()) {
            return toast.showError("Field name cannot be empty");
        }
        const fieldId = newFieldName.toLowerCase().replace(/[^a-z0-9]/g, "_");
        if (availableFields.some(f => f.id === fieldId)) {
            return toast.showError("A field with this identifier already exists");
        }

        const newField: DesignerField = {
            id: fieldId,
            label: newFieldName.trim(),
            isCustom: true
        };

        setCustomFields(prev => [...prev, newField]);
        toast.showSuccess(`Added custom field: ${newFieldName}`);
        setNewFieldName("");
    };

    // Copy/Duplicate an existing field
    const handleCopyField = (originalFieldId: string) => {
        let nextCopyNum = 1;
        const baseFieldId = originalFieldId.replace(/_copy_\d+$/, "");

        const existingIds = new Set<string>();
        if (printType === "Transaction Receipt") {
            if (rawConfig?.fields) {
                Object.keys(rawConfig.fields).forEach((id: string) => existingIds.add(id));
            }
        } else {
            if (Array.isArray(rawConfig?.required_fields)) {
                rawConfig.required_fields.forEach((id: any) => existingIds.add(String(id)));
            }
        }

        while (existingIds.has(`${baseFieldId}_copy_${nextCopyNum}`)) {
            nextCopyNum++;
        }

        const copyId = `${baseFieldId}_copy_${nextCopyNum}`;

        setRawConfig((prev: any) => {
            const copy = { ...prev };
            if (printType === "Transaction Receipt") {
                if (!copy.fields) copy.fields = {};

                const origFieldCfg = copy.fields[originalFieldId] || {};
                copy.fields[copyId] = {
                    ...origFieldCfg,
                    left: "1.0",
                    top: "1.0",
                    visible: true
                };
            } else {
                if (!copy.required_fields) copy.required_fields = [];
                copy.required_fields.push(copyId);

                const origFieldStyle = copy[originalFieldId] || {};
                copy[copyId] = { ...origFieldStyle };

                copy[`${copyId}_left`] = "1.0cm";
                copy[`${copyId}_top`] = "1.0cm";

                if (copy[`${originalFieldId}_show_label`]) {
                    copy[`${copyId}_show_label`] = true;
                    copy[`${copyId}_label_left`] = "1.0cm";
                    copy[`${copyId}_label_top`] = "1.0cm";
                }

                ["en", "si", "ta"].forEach(lang => {
                    const customLabel = copy[`${originalFieldId}_label_${lang}`];
                    if (customLabel !== undefined) {
                        copy[`${copyId}_label_${lang}`] = customLabel;
                    }
                });
            }
            return copy;
        });

        toast.showSuccess(`Created copy: ${copyId}`);
    };

    // Delete a duplicated field copy
    const handleDeleteCopyField = (copyFieldId: string) => {
        if (!copyFieldId.includes("_copy_")) return;

        setRawConfig((prev: any) => {
            const copy = { ...prev };
            if (printType === "Transaction Receipt") {
                if (copy.fields && copy.fields[copyFieldId]) {
                    delete copy.fields[copyFieldId];
                }
            } else {
                if (Array.isArray(copy.required_fields)) {
                    copy.required_fields = copy.required_fields.filter((id: string) => id !== copyFieldId);
                }

                delete copy[copyFieldId];
                delete copy[`${copyFieldId}_left`];
                delete copy[`${copyFieldId}_top`];
                delete copy[`${copyFieldId}_show_label`];
                delete copy[`${copyFieldId}_label_left`];
                delete copy[`${copyFieldId}_label_top`];
                ["en", "si", "ta"].forEach(lang => {
                    delete copy[`${copyFieldId}_label_${lang}`];
                });
            }
            return copy;
        });

        if (selectedElement && selectedElement.id === copyFieldId) {
            setSelectedElement(null);
        }

        toast.showSuccess(`Removed copy: ${copyFieldId}`);
    };

    const handleBackup = async () => {
        setIsBackingUp(true);
        try {
            const res = await printPolicyService.backupPolicy();
            toast.showSuccess(`Backup created successfully: ${res.filename}`);
            const list = await printPolicyService.getBackups();
            setBackups(list);
            if (list && list.length > 0) {
                setSelectedBackup(list[0]);
            }
        } catch (err: any) {
            toast.showError(err.message || "Failed to create backup");
        } finally {
            setIsBackingUp(false);
        }
    };

    const handleRestore = async () => {
        if (!selectedBackup) {
            toast.showError("Please select a backup file first");
            return;
        }
        const confirmRestore = window.confirm(
            `Are you sure you want to import "${selectedBackup}"? This will REPLACE ALL layouts in the database with this backup data.`
        );
        if (!confirmRestore) return;

        setIsRestoring(true);
        try {
            const res = await printPolicyService.restoreBackup(selectedBackup);
            toast.showSuccess(res.detail || "Backup imported successfully");
            window.location.reload();
        } catch (err: any) {
            toast.showError(err.message || "Failed to import backup");
        } finally {
            setIsRestoring(false);
        }
    };

    // Sub-section management
    const handleAddSubSection = () => {
        const name = window.prompt("Enter sub-section name:");
        if (!name || !name.trim()) return;
        const trimmed = name.trim();
        if (subSections.includes(trimmed)) {
            toast.showError(`Sub-section "${trimmed}" already exists`);
            return;
        }
        const newSubConfig = JSON.parse(JSON.stringify(rawConfig));
        let updated: Record<string, any>;
        const isPayment = printType === "Transaction Receipt" && txTypes.find(t => String(t.id) === txTypeId)?.type_name === "Payment";
        const useArraySubSections = printType === "Transaction Receipt" || (["TRANSACTIONS", "FD_CERTIFICATE", "PAWNING_MANDATE", "HEADING", "OPENING_BALANCE"].includes(category));

        if (useArraySubSections) {
            const categoryConfig = (isPayment 
                ? (fullConfig[subCategory] || {}) 
                : fullConfig) as any;

            let updatedCategoryConfig: any;

            if (subSections.length === 1) {
                // First custom sub-section: add sub_sections key
                updatedCategoryConfig = { ...rawConfig, sub_sections: [{ [trimmed]: newSubConfig }] };
            } else {
                const existingSubSections = [...(categoryConfig.sub_sections || [])];
                existingSubSections.push({ [trimmed]: newSubConfig });
                updatedCategoryConfig = { ...categoryConfig, sub_sections: existingSubSections };
            }

            if (isPayment) {
                updated = {
                    ...fullConfig,
                    [subCategory]: updatedCategoryConfig
                };
            } else {
                updated = updatedCategoryConfig;
            }
        } else {
            if (subSections.length === 1) {
                // First custom sub-section: add sub_categories key
                updated = { ...rawConfig, sub_categories: { [trimmed]: newSubConfig } };
            } else {
                const existingSubCats = { ...(fullConfig.sub_categories || {}) };
                existingSubCats[trimmed] = newSubConfig;
                updated = { ...fullConfig, sub_categories: existingSubCats };
            }
        }
        setFullConfig(updated);
        setSubSections(prev => [...prev, trimmed]);
        setActiveSubSection(trimmed);
    };

    const handleRemoveSubSection = (name: string) => {
        if (name === "default") return;
        const confirmRemove = window.confirm(`Delete sub-section "${name}"? This will permanently remove its config.`);
        if (!confirmRemove) return;
        const isPayment = printType === "Transaction Receipt" && txTypes.find(t => String(t.id) === txTypeId)?.type_name === "Payment";
        const useArraySubSections = printType === "Transaction Receipt" || ["TRANSACTIONS", "FD_CERTIFICATE", "PAWNING_MANDATE", "HEADING", "OPENING_BALANCE"].includes(category);

        if (useArraySubSections) {
            const categoryConfig = (isPayment 
                ? (fullConfig[subCategory] || {}) 
                : fullConfig) as any;

            const existingSubSections = (categoryConfig.sub_sections || []).filter((obj: any) => Object.keys(obj)[0] !== name);
            const defaultConfig = { ...categoryConfig };
            delete defaultConfig.sub_sections;

            let updatedCategoryConfig: any;

            if (existingSubSections.length === 0) {
                updatedCategoryConfig = defaultConfig;
                setSubSections(["default"]);
                setActiveSubSection("default");
                setRawConfig(defaultConfig);
                setTemplateImage(defaultConfig.background_image || null);
                if (defaultConfig.page_width) setPageWidthCm(defaultConfig.page_width.replace("cm", ""));
                if (defaultConfig.page_height) setPageHeightCm(defaultConfig.page_height.replace("cm", ""));
            } else {
                updatedCategoryConfig = { ...defaultConfig, sub_sections: existingSubSections };
                const subKeys = existingSubSections.map((obj: any) => Object.keys(obj)[0]);
                setSubSections(["default", ...subKeys]);
                setActiveSubSection("default");
                setRawConfig(defaultConfig);
                setTemplateImage(defaultConfig.background_image || null);
                if (defaultConfig.page_width) setPageWidthCm(defaultConfig.page_width.replace("cm", ""));
                if (defaultConfig.page_height) setPageHeightCm(defaultConfig.page_height.replace("cm", ""));
            }

            let updated: any;
            if (isPayment) {
                updated = {
                    ...fullConfig,
                    [subCategory]: updatedCategoryConfig
                };
            } else {
                updated = updatedCategoryConfig;
            }
            setFullConfig(updated);
        } else {
            const existingSubCats = { ...(fullConfig.sub_categories || {}) };
            delete existingSubCats[name];
            const defaultConfig = { ...fullConfig };
            delete defaultConfig.sub_categories;
            if (Object.keys(existingSubCats).length === 0) {
                // No more custom sub-sections: transition to flat mode
                setFullConfig({});
                setSubSections(["default"]);
                setActiveSubSection("default");
                setRawConfig(defaultConfig);
                setTemplateImage(defaultConfig.background_image || null);
                if (defaultConfig.page_width) setPageWidthCm(defaultConfig.page_width.replace("cm", ""));
                if (defaultConfig.page_height) setPageHeightCm(defaultConfig.page_height.replace("cm", ""));
            } else {
                const updated = { ...defaultConfig, sub_categories: existingSubCats };
                setFullConfig(updated);
                setSubSections(["default", ...Object.keys(existingSubCats)]);
                setActiveSubSection("default");
                setRawConfig(defaultConfig);
                setTemplateImage(defaultConfig.background_image || null);
                if (defaultConfig.page_width) setPageWidthCm(defaultConfig.page_width.replace("cm", ""));
                if (defaultConfig.page_height) setPageHeightCm(defaultConfig.page_height.replace("cm", ""));
            }
        }
        toast.showSuccess(`Sub-section "${name}" has been removed`);
    };

    const switchSubSection = (name: string) => {
        if (name === activeSubSection) return;
        // Save current config into fullConfig before switching
        let updated: Record<string, any>;
        const isPayment = printType === "Transaction Receipt" && txTypes.find(t => String(t.id) === txTypeId)?.type_name === "Payment";
        const useArraySubSections = printType === "Transaction Receipt" || ["TRANSACTIONS", "FD_CERTIFICATE", "PAWNING_MANDATE", "HEADING", "OPENING_BALANCE"].includes(category);

        if (useArraySubSections) {
            const categoryConfig = (isPayment 
                ? (fullConfig[subCategory] || {}) 
                : fullConfig) as any;

            const existingSubSections = categoryConfig.sub_sections || [];
            let updatedSubSections = [...existingSubSections];
            let updatedCategoryConfig: any;

            if (activeSubSection === "default") {
                updatedCategoryConfig = { ...rawConfig, sub_sections: existingSubSections };
            } else {
                let updatedItem = false;
                updatedSubSections = existingSubSections.map((obj: any) => {
                    if (Object.keys(obj)[0] === activeSubSection) {
                        updatedItem = true;
                        return { [activeSubSection]: { ...rawConfig } };
                    }
                    return obj;
                });
                if (!updatedItem) {
                    updatedSubSections.push({ [activeSubSection]: { ...rawConfig } });
                }
                const defaultConfig = { ...categoryConfig };
                delete defaultConfig.sub_sections;
                updatedCategoryConfig = { ...defaultConfig, sub_sections: updatedSubSections };
            }

            if (isPayment) {
                updated = {
                    ...fullConfig,
                    [subCategory]: updatedCategoryConfig
                };
            } else {
                updated = updatedCategoryConfig;
            }

            setFullConfig(updated);
            setActiveSubSection(name);

            // Load target config directly from memory
            const finalCategoryConfig = isPayment ? (updated[subCategory] || {}) : updated;
            if (name === "default") {
                const defaultConfig = { ...finalCategoryConfig };
                delete defaultConfig.sub_sections;
                setRawConfig(defaultConfig);
                setTemplateImage(defaultConfig.background_image || null);
                if (printType === "Transaction Receipt") {
                    if (defaultConfig.page?.width) setPageWidthCm(defaultConfig.page.width.replace("cm", ""));
                    if (defaultConfig.page?.height) setPageHeightCm(defaultConfig.page.height.replace("cm", ""));
                } else {
                    if (defaultConfig.page_width) setPageWidthCm(defaultConfig.page_width.replace("cm", ""));
                    if (defaultConfig.page_height) setPageHeightCm(defaultConfig.page_height.replace("cm", ""));
                }
            } else {
                const targetObj = finalCategoryConfig.sub_sections?.find((obj: any) => Object.keys(obj)[0] === name);
                const targetConfig = targetObj ? targetObj[name] : {};
                setRawConfig(targetConfig);
                setTemplateImage(targetConfig.background_image || null);
                if (printType === "Transaction Receipt") {
                    if (targetConfig.page?.width) setPageWidthCm(targetConfig.page.width.replace("cm", ""));
                    if (targetConfig.page?.height) setPageHeightCm(targetConfig.page.height.replace("cm", ""));
                } else {
                    if (targetConfig.page_width) setPageWidthCm(targetConfig.page_width.replace("cm", ""));
                    if (targetConfig.page_height) setPageHeightCm(targetConfig.page_height.replace("cm", ""));
                }
            }
        } else {
            if (activeSubSection === "default") {
                const existingSubCats = fullConfig.sub_categories || {};
                updated = { ...rawConfig, sub_categories: existingSubCats };
            } else {
                const existingSubCats = { ...(fullConfig.sub_categories || {}) };
                existingSubCats[activeSubSection] = { ...rawConfig };
                const defaultConfig = { ...fullConfig };
                delete defaultConfig.sub_categories;
                updated = { ...defaultConfig, sub_categories: existingSubCats };
            }
            setFullConfig(updated);
            setActiveSubSection(name);
            // Load target config directly from memory
            if (name === "default") {
                const defaultConfig = { ...updated };
                delete defaultConfig.sub_categories;
                setRawConfig(defaultConfig);
                setTemplateImage(defaultConfig.background_image || null);
                if (defaultConfig.page_width) setPageWidthCm(defaultConfig.page_width.replace("cm", ""));
                if (defaultConfig.page_height) setPageHeightCm(defaultConfig.page_height.replace("cm", ""));
            } else {
                const targetConfig = updated.sub_categories?.[name] || {};
                setRawConfig(targetConfig);
                setTemplateImage(targetConfig.background_image || null);
                if (targetConfig.page_width) setPageWidthCm(targetConfig.page_width.replace("cm", ""));
                if (targetConfig.page_height) setPageHeightCm(targetConfig.page_height.replace("cm", ""));
            }
        }
    };

    // Save configuration
    const handleSave = async () => {
        setSaving(true);
        try {
            if (printType === "Transaction Receipt") {
                if (!txTypeId) return;
                const isPayment = txTypes.find(t => String(t.id) === txTypeId)?.type_name === "Payment";

                const subConfigToSave = { ...rawConfig };
                subConfigToSave.page = {
                    width: pageWidthCm + "cm",
                    height: pageHeightCm + "cm"
                };

                                                const currentFullConfig = (await getTransactionTypePrintConfig(Number(txTypeId))) || {};
                const categoryConfig = (isPayment 
                    ? (currentFullConfig[subCategory] || {}) 
                    : currentFullConfig) as any;

                let updatedCategoryConfig: any;
                let finalConfigToSave: any;

                if (subSections.length > 1) {
                    const existingSubSections = categoryConfig.sub_sections || [];
                    if (activeSubSection === "default") {
                        updatedCategoryConfig = { ...subConfigToSave, sub_sections: existingSubSections };
                    } else {
                        let defaultConfig = { ...categoryConfig } as any;
                        delete defaultConfig.sub_sections;
                        if (Object.keys(defaultConfig).length === 0) {
                            const memDefault = { ... (isPayment ? (fullConfig?.[subCategory] || {}) : (fullConfig || {})) } as any;
                            delete memDefault.sub_sections;
                            if (Object.keys(memDefault).length > 0) {
                                defaultConfig = memDefault;
                            }
                        }
                        let updated = false;
                        const updatedSubSections = existingSubSections.map((obj: any) => {
                            if (Object.keys(obj)[0] === activeSubSection) {
                                updated = true;
                                return { [activeSubSection]: subConfigToSave };
                            }
                            return obj;
                        });
                        if (!updated) {
                            updatedSubSections.push({ [activeSubSection]: subConfigToSave });
                        }
                        updatedCategoryConfig = { ...defaultConfig, sub_sections: updatedSubSections };
                    }
                } else {
                    if (isPayment) {
                        updatedCategoryConfig = subConfigToSave;
                    } else {
                        updatedCategoryConfig = subConfigToSave;
                    }
                }

                if (isPayment) {
                    finalConfigToSave = {
                        ...currentFullConfig,
                        [subCategory]: updatedCategoryConfig
                    };
                } else {
                    finalConfigToSave = updatedCategoryConfig;
                }
                await saveTransactionTypePrintConfig(Number(txTypeId), finalConfigToSave);
                setFullConfig(finalConfigToSave);
            } else {
                const passbookPolicyId = Number(printType);

                const parsedPassbookType = (printType === "3") ? 1 : (printType === "4") ? 2 : Number(passbookType);
                const finalConfig = { ...rawConfig };
                finalConfig.page_width = pageWidthCm + "cm";
                finalConfig.page_height = pageHeightCm + "cm";

                const resolvedCategory = category;

                let saveConfigStr: string;
                const isNewSubCategory = printType !== "Transaction Receipt" && ["TRANSACTIONS", "FD_CERTIFICATE", "PAWNING_MANDATE", "HEADING", "OPENING_BALANCE"].includes(category);
                if (category === "FRONT_PAGE" && subSections.length > 1) {
                    const existingSubCats = fullConfig.sub_categories || {};
                    if (activeSubSection === "default") {
                        // Save default: flat config + keep existing sub_categories
                        saveConfigStr = JSON.stringify({ ...finalConfig, sub_categories: existingSubCats });
                    } else {
                        // Save custom sub-section: keep default config, update sub_categories
                        const defaultConfig = { ...fullConfig };
                        delete defaultConfig.sub_categories;
                        const updatedSubCats = { ...existingSubCats, [activeSubSection]: finalConfig };
                        saveConfigStr = JSON.stringify({ ...defaultConfig, sub_categories: updatedSubCats });
                    }
                } else if (isNewSubCategory && subSections.length > 1) {
                    const existingSubSections = fullConfig.sub_sections || [];
                    if (activeSubSection === "default") {
                        saveConfigStr = JSON.stringify({ ...finalConfig, sub_sections: existingSubSections });
                    } else {
                        const defaultConfig = { ...fullConfig };
                        delete defaultConfig.sub_sections;

                        let updated = false;
                        const updatedSubSections = existingSubSections.map((obj: any) => {
                            if (Object.keys(obj)[0] === activeSubSection) {
                                updated = true;
                                return { [activeSubSection]: finalConfig };
                            }
                            return obj;
                        });
                        if (!updated) {
                            updatedSubSections.push({ [activeSubSection]: finalConfig });
                        }
                        saveConfigStr = JSON.stringify({ ...defaultConfig, sub_sections: updatedSubSections });
                    }
                } else {
                    saveConfigStr = JSON.stringify(finalConfig);
                }

                await printPolicyService.createOrUpdatePolicy({
                    passbook_policy_id: passbookPolicyId,
                    passbook_type: parsedPassbookType!,
                    category: resolvedCategory,
                    config: saveConfigStr
                });
            }
            toast.showSuccess("Print layout configuration saved successfully");
        } catch (err: any) {
            toast.showError(err.message || "Failed to save configurations");
        } finally {
            setSaving(false);
        }
    };

    // Table calculations for visual representation
    const tableRowHeightCm = rawConfig.sections?.table?.row_height || "0.8cm";
    const tableHeightPx = getCoordinateValue(tableRowHeightCm) * 3; // 3 mock rows, in cm

    const canvasBgImage = [
        showGrid ? 'linear-gradient(to right, rgba(13, 110, 253, 0.15) 1px, transparent 1px)' : null,
        showGrid ? 'linear-gradient(to bottom, rgba(13, 110, 253, 0.15) 1px, transparent 1px)' : null,
        templateImage ? `url(${templateImage})` : null
    ].filter(Boolean).join(', ') || 'none';

    const canvasBgSize = [
        showGrid ? `0.5cm 0.5cm` : null,
        showGrid ? `0.5cm 0.5cm` : null,
        templateImage ? '100% 100%' : null
    ].filter(Boolean).join(', ') || 'auto';

    const canvasBgRepeat = [
        showGrid ? 'repeat' : null,
        showGrid ? 'repeat' : null,
        templateImage ? 'no-repeat' : null
    ].filter(Boolean).join(', ') || 'no-repeat';

    const canvasBgPosition = [
        showGrid ? 'top left' : null,
        showGrid ? 'top left' : null,
        templateImage ? 'center' : null
    ].filter(Boolean).join(', ') || 'center';

    return (
        <Box padding="medium" style={{ background: "#f8f9fa", minHeight: "100vh" }}>
            <Columns space="medium" collapseBelow="tablet">

                {/* Left Sidebar Config Panel */}
                <Column width="1/3">
                    <Stack space="medium">

                        <Card>
                            <Stack space="small">
                                <Heading level="3">1. Select Target Template</Heading>
                                <Dropdown
                                    label="Print Type"
                                    value={printType}
                                    onChange={e => {
                                        const val = e.currentTarget.value;
                                        setPrintType(val);
                                        if (val === "3") {
                                            setPassbookType("1");
                                            setCategory("FRONT_PAGE");
                                        } else if (val === "4") {
                                            setPassbookType("2");
                                            setCategory("FRONT_PAGE");
                                        } else if (val === "1" || val === "2") {
                                            if (categories.length > 0) {
                                                setPassbookType(String(categories[0].id));
                                            }
                                        }
                                    }}
                                >
                                    <option value="1">Passbook Normal</option>
                                    <option value="2">Passcard Normal</option>
                                    <option value="3">Passbook/Passcard with FD Certificate</option>
                                    <option value="4">Passbook/Passcard with Pawning Mandate</option>
                                    <option value="Transaction Receipt">Transactions</option>
                                </Dropdown>

                                {printType !== "Transaction Receipt" && (
                                    <Dropdown
                                        label="Account Product Type"
                                        value={printType === "3" ? "1" : printType === "4" ? "2" : passbookType}
                                        onChange={e => setPassbookType(e.currentTarget.value)}
                                        disabled={printType === "3" || printType === "4"}
                                    >
                                        {categories.map(c => (
                                            <option key={c.id} value={String(c.id)}>{c.category_name}</option>
                                        ))}
                                    </Dropdown>
                                )}

                                {printType !== "Transaction Receipt" && (
                                    <Dropdown
                                        label="Category Section"
                                        value={category}
                                        onChange={e => {
                                            const val = e.currentTarget.value;
                                            setCategory(val);
                                            const allowedNewSub = ["TRANSACTIONS", "FD_CERTIFICATE", "PAWNING_MANDATE", "HEADING", "OPENING_BALANCE"].includes(val);
                                            if (val !== "FRONT_PAGE" && !allowedNewSub) {
                                                setActiveSubSection("default");
                                                setSubSections(["default"]);
                                                setFullConfig({});
                                            }
                                        }}
                                    >
                                        {[
                                            <option key="FRONT_PAGE" value="FRONT_PAGE">Front Page</option>,
                                            <option key="HEADING" value="HEADING">Header Layout</option>,
                                            <option key="TRANSACTIONS" value="TRANSACTIONS">Transactions</option>,
                                            <option key="OPENING_BALANCE" value="OPENING_BALANCE">Opening Balance</option>,
                                            ...(printType === "3" ? [<option key="FD_CERTIFICATE" value="FD_CERTIFICATE">FD Certificate</option>] : []),
                                            ...(printType === "4" ? [<option key="PAWNING_MANDATE" value="PAWNING_MANDATE">Pawning Mandate</option>] : [])
                                        ]}
                                    </Dropdown>
                                )}
                                {(category === "TRANSACTIONS" || category === "OPENING_BALANCE") && (
                                    <Box paddingTop="xsmall">
                                        <Button
                                            variant="ghost"
                                            tone="brandAccent"
                                            onClick={() => {
                                                const fields = getRequiredFields();
                                                setRawConfig((prev: any) => {
                                                    const copy = { ...prev };
                                                    fields.forEach((fieldId) => {
                                                        if (!copy[fieldId]) {
                                                            copy[fieldId] = {};
                                                        }
                                                        // 1. Bring all columns to 2.0cm default width
                                                        copy[fieldId].column_width = "2.0cm";
                                                        // 2. Clear any individual drag offsets so they align back to the left side
                                                        delete copy[`${fieldId}_left`];
                                                        delete copy[`${fieldId}_top`];
                                                    });
                                                    return copy;
                                                });
                                                toast.showSuccess("Columns reset to 2.0cm width and aligned sequentially to the left side.");
                                            }}
                                        >
                                            Reset Columns layout (2cm Left)
                                        </Button>
                                    </Box>
                                )}
                                {printType === "Transaction Receipt" && (
                                    <Dropdown
                                        label="Transaction Type"
                                        value={txTypeId}
                                        onChange={e => setTxTypeId(e.currentTarget.value)}
                                    >
                                        {txTypes.map(t => (
                                            <option key={t.id} value={String(t.id)}>{t.type_name}</option>
                                        ))}
                                    </Dropdown>
                                )}
                                {printType === "Transaction Receipt" && txTypes.find(t => String(t.id) === txTypeId)?.type_name === "Payment" && (
                                    <Dropdown
                                        label="Withdraw and Payments"
                                        value={subCategory}
                                        onChange={e => setSubCategory(e.currentTarget.value)}
                                    >
                                        <option value="withdraw">Withdraw</option>
                                        <option value="payment">Payment</option>
                                    </Dropdown>
                                )}
                                {(printType === "Transaction Receipt" || category === "FRONT_PAGE" || ["TRANSACTIONS", "FD_CERTIFICATE", "PAWNING_MANDATE", "HEADING", "OPENING_BALANCE"].includes(category)) && (
                                    <Box paddingTop="small">
                                        <Stack space="small">
                                            <RadioGroup
                                                label="Sub Section"
                                                value={activeSubSection}
                                                onChange={e => switchSubSection(e.currentTarget.value)}
                                            >
                                                {["default", ...subSections.filter(s => s !== "default")].map(s => (
                                                    <RadioItem key={s} label={s} value={s} />
                                                ))}
                                            </RadioGroup>
                                            <Inline space="xsmall">
                                                <Button size="small" variant="ghost" tone="brandAccent" onClick={handleAddSubSection}>
                                                    + Add Sub Section
                                                </Button>
                                                {activeSubSection !== "default" && (
                                                    <Button size="small" variant="ghost" tone="critical" onClick={() => handleRemoveSubSection(activeSubSection)}>
                                                        Remove
                                                    </Button>
                                                )}
                                            </Inline>
                                        </Stack>
                                    </Box>
                                )}
                            </Stack>
                        </Card>

                        <Card>
                            <Stack space="small">
                                <Heading level="3">{(category === "TRANSACTIONS" || category === "OPENING_BALANCE") ? "2. Page Dimensions & Margins (cm)" : "2. Page Dimensions (cm)"}</Heading>
                                <Columns space="small">
                                    <Column>
                                        <TextField
                                            label="Page Width (cm)"
                                            id="page_width_cm"
                                            value={pageWidthCm}
                                            onChange={e => {
                                                const val = e.currentTarget.value;
                                                setPageWidthCm(val);
                                                setRawConfig((prev: any) => {
                                                    const copy = { ...prev };
                                                    if (printType === "Transaction Receipt") {
                                                        if (!copy.page) copy.page = {};
                                                        copy.page.width = val + "cm";
                                                    } else {
                                                        copy.page_width = val + "cm";
                                                    }
                                                    return copy;
                                                });
                                            }}
                                        />
                                    </Column>
                                    <Column>
                                        <TextField
                                            label="Page Height (cm)"
                                            id="page_height_cm"
                                            value={pageHeightCm}
                                            onChange={e => {
                                                const val = e.currentTarget.value;
                                                setPageHeightCm(val);
                                                setRawConfig((prev: any) => {
                                                    const copy = { ...prev };
                                                    if (printType === "Transaction Receipt") {
                                                        if (!copy.page) copy.page = {};
                                                        copy.page.height = val + "cm";
                                                    } else {
                                                        copy.page_height = val + "cm";
                                                    }
                                                    return copy;
                                                });
                                            }}
                                        />
                                    </Column>
                                </Columns>
                                <Dropdown
                                    label="Date Format"
                                    value={rawConfig.date_format || "yyyy-MM-dd"}
                                    onChange={e => {
                                        const val = e.currentTarget.value;
                                        setRawConfig((prev: any) => ({
                                            ...prev,
                                            date_format: val
                                        }));
                                    }}
                                >
                                    <option value="yyyy-MM-dd">yyyy-MM-dd (e.g. 2026-06-18)</option>
                                    <option value="yy-MM-dd">yy-MM-dd (e.g. 26-06-18)</option>
                                    <option value="dd-MM-yyyy">dd-MM-yyyy (e.g. 18-06-2026)</option>
                                    <option value="dd-MM-yy">dd-MM-yy (e.g. 18-06-26)</option>
                                    <option value="yyyy MM dd">yyyy MM dd (e.g. 2026 06 18)</option>
                                    <option value="dd MM yyyy">dd MM yyyy (e.g. 18 06 2026)</option>
                                    <option value="yyyy/MM/dd">yyyy/MM/dd (e.g. 2026/06/18)</option>
                                    <option value="dd/MM/yyyy">dd/MM/yyyy (e.g. 18/06/2026)</option>
                                    <option value="yyyy MMM dd">yyyy MMM dd (e.g. 2026 Jun 18)</option>
                                    <option value="dd MMM yyyy">dd MMM yyyy (e.g. 18 Jun 2026)</option>
                                    <optgroup label="Date & Time (24h)">
                                        <option value="yyyy-MM-dd HH:mm">yyyy-MM-dd HH:mm (e.g. 2026-06-18 14:30)</option>
                                        <option value="dd-MM-yyyy HH:mm">dd-MM-yyyy HH:mm (e.g. 18-06-2026 14:30)</option>
                                        <option value="yyyy/MM/dd HH:mm">yyyy/MM/dd HH:mm (e.g. 2026/06/18 14:30)</option>
                                        <option value="dd/MM/yyyy HH:mm">dd/MM/yyyy HH:mm (e.g. 18/06/2026 14:30)</option>
                                        <option value="yyyy MMM dd HH:mm">yyyy MMM dd HH:mm (e.g. 2026 Jun 18 14:30)</option>
                                        <option value="dd MMM yyyy HH:mm">dd MMM yyyy HH:mm (e.g. 18 Jun 2026 14:30)</option>
                                        <option value="yyyy-MM-dd HH:mm:ss">yyyy-MM-dd HH:mm:ss (e.g. 2026-06-18 14:30:00)</option>
                                    </optgroup>
                                    <optgroup label="Date & Time (AM/PM)">
                                        <option value="yyyy-MM-dd hh:mm a">yyyy-MM-dd hh:mm a (e.g. 2026-06-18 02:30 PM)</option>
                                        <option value="dd-MM-yyyy hh:mm a">dd-MM-yyyy hh:mm a (e.g. 18-06-2026 02:30 PM)</option>
                                        <option value="yyyy MMM dd hh:mm a">yyyy MMM dd hh:mm a (e.g. 2026 Jun 18 02:30 PM)</option>
                                        <option value="dd MMM yyyy hh:mm a">dd MMM yyyy hh:mm a (e.g. 18 Jun 2026 02:30 PM)</option>
                                    </optgroup>
                                </Dropdown>
                                <Dropdown
                                    label="Time Format (for Print Time field)"
                                    value={rawConfig.time_format || "HH:mm:ss"}
                                    onChange={e => {
                                        const val = e.currentTarget.value;
                                        setRawConfig((prev: any) => ({
                                            ...prev,
                                            time_format: val
                                        }));
                                    }}
                                >
                                    <option value="HH:mm:ss">HH:mm:ss (e.g. 14:30:00)</option>
                                    <option value="HH:mm">HH:mm (e.g. 14:30)</option>
                                    <option value="hh:mm:ss a">hh:mm:ss a (e.g. 02:30:00 PM)</option>
                                    <option value="hh:mm a">hh:mm a (e.g. 02:30 PM)</option>
                                </Dropdown>
                                <Dropdown
                                    id="default_lang"
                                    label="Default Language"
                                    value={rawConfig.default_lang || "en"}
                                    onChange={e => {
                                        const val = e.currentTarget.value;
                                        setRawConfig((prev: any) => ({
                                            ...prev,
                                            default_lang: val
                                        }));
                                    }}
                                >
                                    <option value="en">English (EN)</option>
                                    <option value="si">Sinhala (SI)</option>
                                    <option value="ta">Tamil (TA)</option>
                                </Dropdown>
                                <Checkbox
                                    id="hide_na"
                                    label="Hide 'N/A' values on print"
                                    checked={!!rawConfig.hide_na}
                                    onChange={() => {
                                        setRawConfig((prev: any) => ({
                                            ...prev,
                                            hide_na: !prev.hide_na
                                        }));
                                    }}
                                />
                                {(category === "TRANSACTIONS" || category === "OPENING_BALANCE") && (
                                    <>
                                        <Columns space="small">
                                            <Column>
                                                <TextField
                                                    label="Margin Top (cm)"
                                                    id="margin_top"
                                                    value={String(rawConfig.margin_top || "").replace("cm", "")}
                                                    onChange={e => {
                                                        const val = e.currentTarget.value;
                                                        setRawConfig((prev: any) => ({
                                                            ...prev,
                                                            margin_top: val === "" ? "" : val.endsWith("cm") ? val : val + "cm"
                                                        }));
                                                    }}
                                                />
                                            </Column>
                                            <Column>
                                                <TextField
                                                    label="Margin Bottom (cm)"
                                                    id="margin_bottom"
                                                    value={String(rawConfig.margin_bottom || "").replace("cm", "")}
                                                    onChange={e => {
                                                        const val = e.currentTarget.value;
                                                        setRawConfig((prev: any) => ({
                                                            ...prev,
                                                            margin_bottom: val === "" ? "" : val.endsWith("cm") ? val : val + "cm"
                                                        }));
                                                    }}
                                                />
                                            </Column>
                                        </Columns>
                                        <Columns space="small">
                                            <Column>
                                                <TextField
                                                    label="Margin Left (cm)"
                                                    id="margin_left"
                                                    value={String(rawConfig.margin_left || "").replace("cm", "")}
                                                    onChange={e => {
                                                        const val = e.currentTarget.value;
                                                        setRawConfig((prev: any) => ({
                                                            ...prev,
                                                            margin_left: val === "" ? "" : val.endsWith("cm") ? val : val + "cm"
                                                        }));
                                                    }}
                                                />
                                            </Column>
                                            <Column>
                                                <TextField
                                                    label="Margin Right (cm)"
                                                    id="margin_right"
                                                    value={String(rawConfig.margin_right || "").replace("cm", "")}
                                                    onChange={e => {
                                                        const val = e.currentTarget.value;
                                                        setRawConfig((prev: any) => ({
                                                            ...prev,
                                                            margin_right: val === "" ? "" : val.endsWith("cm") ? val : val + "cm"
                                                        }));
                                                    }}
                                                />
                                            </Column>
                                        </Columns>
                                        <Columns space="small">
                                            <Column>
                                                <TextField
                                                    label="Line Height (cm)"
                                                    id="line_height"
                                                    value={String(rawConfig.line_height || "").replace("cm", "")}
                                                    onChange={e => {
                                                        const val = e.currentTarget.value;
                                                        setRawConfig((prev: any) => ({
                                                            ...prev,
                                                            line_height: val === "" ? "" : val.endsWith("cm") ? val : val + "cm"
                                                        }));
                                                    }}
                                                />
                                            </Column>
                                            <Column>
                                                <TextField
                                                    label="Default Font Size"
                                                    id="font_size"
                                                    value={rawConfig.font_size || ""}
                                                    placeholder="e.g. 12px or 10pt"
                                                    onChange={e => {
                                                        const val = e.currentTarget.value;
                                                        setRawConfig((prev: any) => ({
                                                            ...prev,
                                                            font_size: val
                                                        }));
                                                    }}
                                                />
                                            </Column>
                                        </Columns>
                                    </>
                                )}
                            </Stack>
                        </Card>
                        {(printType === "Transaction Receipt" || category === "PAWNING_MANDATE") && (() => {
                            const currentTableId = selectedElement && (selectedElement.id === "table_section" || selectedElement.id.startsWith("table_section_")) ? selectedElement.id : "table_section";
                            const tKey = getTableKey(currentTableId);
                            const defaultCols = getDefaultTableColumns(printType, category);
                            const activeCols = rawConfig.sections?.[tKey]?.columns || defaultCols;

                            return (
                                <Card>
                                    <Stack space="small">
                                        <Heading level="3">Table Section Configuration</Heading>

                                        {/* Table copy / delete options */}
                                        <Box display="flex" justifyContent="spaceBetween" alignItems="center" paddingBottom="small" style={{ gap: '8px' }}>
                                            <select
                                                value={currentTableId}
                                                onChange={(e) => setSelectedElement({ id: e.currentTarget.value, type: "value" })}
                                                style={{
                                                    padding: "4px 8px",
                                                    fontSize: "13px",
                                                    borderRadius: "4px",
                                                    border: "1px solid #c8ccd4",
                                                    backgroundColor: "#fff",
                                                    color: "#2b303a",
                                                    outline: "none",
                                                    cursor: "pointer",
                                                    height: "32px",
                                                    fontWeight: 500,
                                                    flexGrow: 1,
                                                }}
                                            >
                                                <option value="table_section">Main Table Section</option>
                                                {Object.keys(rawConfig.sections || {})
                                                    .filter(k => k.startsWith("table_copy_"))
                                                    .map(k => (
                                                        <option key={k} value={`table_section_${k}`}>
                                                            Table Copy ({k.replace("table_copy_", "")})
                                                        </option>
                                                    ))
                                                }
                                            </select>
                                            <Inline space="xsmall">
                                                <Button
                                                    size="small"
                                                    onClick={handleCopyTableSection}
                                                >
                                                    Copy
                                                </Button>
                                                {currentTableId.startsWith("table_section_") && (
                                                    <Button
                                                        size="small"
                                                        tone="critical"
                                                        onClick={() => handleDeleteTableSection(currentTableId)}
                                                    >
                                                        Delete
                                                    </Button>
                                                )}
                                            </Inline>
                                        </Box>

                                        <Box paddingBottom="xsmall">
                                            <Checkbox
                                                label="Enable Table Section"
                                                id="table_enabled"
                                                checked={rawConfig.sections?.[tKey]?.enabled !== false}
                                                onChange={() => {
                                                    setRawConfig((prev: any) => {
                                                        const copy = { ...prev };
                                                        if (!copy.sections) copy.sections = {};
                                                        if (!copy.sections[tKey]) {
                                                            copy.sections[tKey] = { top: "7.5cm", left: "2cm", width: "15cm", row_height: "0.8cm" };
                                                        }
                                                        copy.sections[tKey].enabled = copy.sections[tKey].enabled === false ? true : false;
                                                        return copy;
                                                    });
                                                }}
                                            />
                                        </Box>

                                        <Columns space="small">
                                            <Column>
                                                <TextField
                                                    label="Table Top (cm)"
                                                    id="table_top"
                                                    value={getTableConfigValue("top", currentTableId)}
                                                    onChange={e => handleTableConfigChange("top", e.currentTarget.value, currentTableId)}
                                                />
                                            </Column>
                                            <Column>
                                                <TextField
                                                    label="Table Left (cm)"
                                                    id="table_left"
                                                    value={getTableConfigValue("left", currentTableId)}
                                                    onChange={e => handleTableConfigChange("left", e.currentTarget.value, currentTableId)}
                                                />
                                            </Column>
                                        </Columns>
                                        <Columns space="small">
                                            <Column>
                                                <TextField
                                                    label="Table Width (cm)"
                                                    id="table_width"
                                                    value={getTableConfigValue("width", currentTableId)}
                                                    onChange={e => handleTableConfigChange("width", e.currentTarget.value, currentTableId)}
                                                />
                                            </Column>
                                            <Column>
                                                <TextField
                                                    label="Table Height (cm)"
                                                    id="table_height"
                                                    value={getTableConfigValue("height", currentTableId)}
                                                    onChange={e => handleTableConfigChange("height", e.currentTarget.value, currentTableId)}
                                                />
                                            </Column>
                                        </Columns>
                                        <Columns space="small">
                                            <Column>
                                                <TextField
                                                    label="Row Height (cm)"
                                                    id="table_row_height"
                                                    value={getTableConfigValue("row_height", currentTableId)}
                                                    onChange={e => handleTableConfigChange("row_height", e.currentTarget.value, currentTableId)}
                                                />
                                            </Column>
                                            <Column><div /></Column>
                                        </Columns>
                                        <Box paddingBottom="xsmall">
                                            <Checkbox
                                                label="Hide Table Header"
                                                id="table_hide_header"
                                                checked={!!rawConfig.sections?.[tKey]?.hide_header}
                                                onChange={() => {
                                                    setRawConfig((prev: any) => {
                                                        const copy = { ...prev };
                                                        if (!copy.sections) copy.sections = {};
                                                        if (!copy.sections[tKey]) {
                                                            copy.sections[tKey] = { top: "7.5cm", left: "2cm", width: "15cm", row_height: "0.8cm" };
                                                        }
                                                        copy.sections[tKey].hide_header = !copy.sections[tKey].hide_header;
                                                        return copy;
                                                    });
                                                }}
                                            />
                                        </Box>
                                        <Box paddingBottom="small">
                                            <Checkbox
                                                label="Hide Table Footer"
                                                id="table_hide_footer"
                                                checked={!!rawConfig.sections?.[tKey]?.hide_footer}
                                                onChange={() => {
                                                    setRawConfig((prev: any) => {
                                                        const copy = { ...prev };
                                                        if (!copy.sections) copy.sections = {};
                                                        if (!copy.sections[tKey]) {
                                                            copy.sections[tKey] = { top: "7.5cm", left: "2cm", width: "15cm", row_height: "0.8cm" };
                                                        }
                                                        copy.sections[tKey].hide_footer = !copy.sections[tKey].hide_footer;
                                                        return copy;
                                                    });
                                                }}
                                            />
                                        </Box>
                                        <Box paddingBottom="small">
                                            <Checkbox
                                                label="Double Line in Footer"
                                                id="table_double_line_footer"
                                                checked={!!rawConfig.sections?.[tKey]?.double_line_footer}
                                                onChange={() => {
                                                    setRawConfig((prev: any) => {
                                                        const copy = { ...prev };
                                                        if (!copy.sections) copy.sections = {};
                                                        if (!copy.sections[tKey]) {
                                                            copy.sections[tKey] = { top: "7.5cm", left: "2cm", width: "15cm", row_height: "0.8cm" };
                                                        }
                                                        copy.sections[tKey].double_line_footer = !copy.sections[tKey].double_line_footer;
                                                        return copy;
                                                    });
                                                }}
                                            />
                                        </Box>
                                        <Box style={{ borderTop: "1px solid #dee2e6" }} paddingTop="medium" paddingBottom="small">
                                            <Text weight="strong" size="small">Active Columns</Text>
                                        </Box>
                                        <Box paddingBottom="medium">
                                            <Inline space="small" alignY="center">
                                                {getAvailableTableColumns(printType, category).map((col) => {
                                                    const checked = activeCols.includes(col.id);
                                                    return (
                                                        <Checkbox
                                                            key={col.id}
                                                            label={col.label}
                                                            id={`col_check_${col.id}`}
                                                            checked={checked}
                                                            onChange={() => {
                                                                setRawConfig((prev: any) => {
                                                                    const copy = { ...prev };
                                                                    if (!copy.sections) copy.sections = {};
                                                                    if (!copy.sections[tKey]) {
                                                                        copy.sections[tKey] = { top: "7.5cm", left: "2cm", width: "15cm", row_height: "0.8cm" };
                                                                    }
                                                                    const active = copy.sections[tKey].columns || defaultCols;
                                                                    let updated: string[];
                                                                    if (active.includes(col.id)) {
                                                                        if (active.length <= 1) return prev;
                                                                        updated = active.filter((c: string) => c !== col.id);
                                                                    } else {
                                                                        updated = [...active, col.id];
                                                                    }
                                                                    copy.sections[tKey].columns = updated;
                                                                    return copy;
                                                                });
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </Inline>
                                        </Box>

                                        {activeCols.map((colId: string) => {
                                            const colLabel = getAvailableTableColumns(printType, category).find(c => c.id === colId)?.label || colId;

                                            return (
                                                <React.Fragment key={colId}>
                                                    <Box style={{ borderTop: "1px solid #dee2e6" }} paddingTop="medium" paddingBottom="small">
                                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                            <Text weight="strong" size="small">{colLabel} Column</Text>
                                                            <div style={{ display: "flex", gap: "4px" }}>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleMoveColumn(colId, -1, currentTableId)}
                                                                    style={{
                                                                        padding: "2px 6px",
                                                                        border: "1px solid #ccc",
                                                                        borderRadius: "4px",
                                                                        background: "#f8f9fa",
                                                                        cursor: "pointer",
                                                                        fontSize: "12px",
                                                                        fontWeight: "bold"
                                                                    }}
                                                                    title="Move Left"
                                                                >
                                                                    ▲
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleMoveColumn(colId, 1, currentTableId)}
                                                                    style={{
                                                                        padding: "2px 6px",
                                                                        border: "1px solid #ccc",
                                                                        borderRadius: "4px",
                                                                        background: "#f8f9fa",
                                                                        cursor: "pointer",
                                                                        fontSize: "12px",
                                                                        fontWeight: "bold"
                                                                    }}
                                                                    title="Move Right"
                                                                >
                                                                    ▼
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </Box>
                                                    <Columns space="small">
                                                        <Column>
                                                            <TextField
                                                                label="Width"
                                                                id={`${colId}_col_width`}
                                                                value={getTableConfigValue(`${colId}_width`, currentTableId)}
                                                                placeholder="e.g. 30% or 4cm"
                                                                onChange={e => handleTableConfigChange(`${colId}_width`, e.currentTarget.value, currentTableId)}
                                                            />
                                                        </Column>
                                                        <Column>
                                                            <Dropdown
                                                                label="Align"
                                                                id={`${colId}_col_align`}
                                                                 value={getTableConfigValue(`${colId}_align`, currentTableId) || (["amount", "balance", "debit", "credit"].includes(colId) ? "right" : "left")}
                                                                onChange={e => handleTableConfigChange(`${colId}_align`, e.currentTarget.value, currentTableId)}
                                                            >
                                                                <option value="left">Left</option>
                                                                <option value="center">Center</option>
                                                                <option value="right">Right</option>
                                                            </Dropdown>
                                                        </Column>
                                                    </Columns>
                                                    <Columns space="small" alignY="bottom">
                                                        <Column>
                                                            <TextField
                                                                label="Font Size"
                                                                id={`${colId}_font_size`}
                                                                value={getTableConfigValue(`${colId}_font_size`, currentTableId)}
                                                                placeholder="e.g. 12px"
                                                                onChange={e => handleTableConfigChange(`${colId}_font_size`, e.currentTarget.value, currentTableId)}
                                                            />
                                                        </Column>
                                                        <Column>
                                                            <Box paddingBottom="xsmall">
                                                                <Inline space="small" alignY="center">
                                                                    <Checkbox
                                                                        label="Bold"
                                                                        id={`${colId}_bold`}
                                                                        checked={getTableConfigValue(`${colId}_bold`, currentTableId) === "true"}
                                                                        onChange={() => handleTableConfigChange(`${colId}_bold`, getTableConfigValue(`${colId}_bold`, currentTableId) === "true" ? "false" : "true", currentTableId)}
                                                                    />
                                                                    <Checkbox
                                                                        label="Italic"
                                                                        id={`${colId}_italic`}
                                                                        checked={getTableConfigValue(`${colId}_italic`, currentTableId) === "true"}
                                                                        onChange={() => handleTableConfigChange(`${colId}_italic`, getTableConfigValue(`${colId}_italic`, currentTableId) === "true" ? "false" : "true", currentTableId)}
                                                                    />
                                                                    <Checkbox
                                                                        label="Translate"
                                                                        id={`${colId}_translate`}
                                                                        checked={getTableConfigValue(`${colId}_translate`, currentTableId) !== "false"}
                                                                        onChange={() => handleTableConfigChange(`${colId}_translate`, getTableConfigValue(`${colId}_translate`, currentTableId) === "false" ? "true" : "false", currentTableId)}
                                                                    />
                                                                </Inline>
                                                            </Box>
                                                        </Column>
                                                    </Columns>
                                                </React.Fragment>
                                            );
                                        })}
                                    </Stack>
                                </Card>
                            );
                        })()}

                        {(category === "TRANSACTIONS" || category === "OPENING_BALANCE") && (
                            <Card>
                                <Stack space="small">
                                    <Heading level="3">4b. Column Order</Heading>
                                    <Text size="small" tone="secondary">Reorder active columns by moving them left or right.</Text>
                                    <Stack space="xsmall">
                                        {getRequiredFields().map((colId, index) => {
                                            const activeFields = category === "OPENING_BALANCE" ? openingBalanceFields : passbookTransactionFields;
                                            const label = activeFields.find(f => f.id === colId)?.label || colId;
                                            return (
                                                <div key={colId} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 8px", border: "1px solid #eee", borderRadius: "4px", background: "#fdfdfd" }}>
                                                    <Text size="small" weight="strong">{label}</Text>
                                                    <div style={{ display: "flex", gap: "4px" }}>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleMoveColumn(colId, -1)}
                                                            disabled={index === 0}
                                                            style={{
                                                                padding: "2px 8px",
                                                                border: "1px solid #ccc",
                                                                borderRadius: "4px",
                                                                background: "#f8f9fa",
                                                                cursor: index === 0 ? "not-allowed" : "pointer",
                                                                opacity: index === 0 ? 0.5 : 1,
                                                                fontSize: "12px",
                                                                fontWeight: "bold"
                                                            }}
                                                            title="Move Left / Up"
                                                        >
                                                            ▲
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleMoveColumn(colId, 1)}
                                                            disabled={index === getRequiredFields().length - 1}
                                                            style={{
                                                                padding: "2px 8px",
                                                                border: "1px solid #ccc",
                                                                borderRadius: "4px",
                                                                background: "#f8f9fa",
                                                                cursor: index === getRequiredFields().length - 1 ? "not-allowed" : "pointer",
                                                                opacity: index === getRequiredFields().length - 1 ? 0.5 : 1,
                                                                fontSize: "12px",
                                                                fontWeight: "bold"
                                                            }}
                                                            title="Move Right / Down"
                                                        >
                                                            ▼
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </Stack>
                                </Stack>
                            </Card>
                        )}

                        {selectedElement && (
                            <Card>
                                <Stack space="small">
                                    {(category === "TRANSACTIONS" || category === "OPENING_BALANCE") ? (
                                        <>
                                            <Heading level="3">Selected Column: {(category === "OPENING_BALANCE" ? openingBalanceFields : passbookTransactionFields).find(f => f.id === selectedElement.id)?.label || selectedElement.id}</Heading>
                                            <TextField
                                                label="Column Width"
                                                id="col_width"
                                                value={rawConfig[selectedElement.id]?.column_width || ""}
                                                placeholder="e.g. 2.0cm"
                                                onChange={e => {
                                                    const val = e.currentTarget.value;
                                                    setRawConfig((prev: any) => {
                                                        const copy = { ...prev };
                                                        if (!copy[selectedElement.id]) copy[selectedElement.id] = {};
                                                        copy[selectedElement.id].column_width = val;
                                                        return copy;
                                                    });
                                                }}
                                                onBlur={e => {
                                                    const val = e.currentTarget.value;
                                                    if (val && !isNaN(Number(val))) {
                                                        setRawConfig((prev: any) => {
                                                            const copy = { ...prev };
                                                            if (!copy[selectedElement.id]) copy[selectedElement.id] = {};
                                                            copy[selectedElement.id].column_width = `${parseFloat(val).toFixed(2)}cm`;
                                                            return copy;
                                                        });
                                                    }
                                                }}
                                            />
                                            <TextField
                                                label="Font Size"
                                                id="sel_font_size"
                                                placeholder="e.g. 12px or 10pt"
                                                value={getFieldStyle(selectedElement.id, "font_size")}
                                                onChange={e => handleFieldStyleChange(selectedElement.id, "font_size", e.currentTarget.value)}
                                            />
                                            <Dropdown
                                                label="Alignment"
                                                id="col_align"
                                                value={rawConfig[selectedElement.id]?.align || ""}
                                                onChange={e => {
                                                    const val = e.currentTarget.value;
                                                    setRawConfig((prev: any) => {
                                                        const copy = { ...prev };
                                                        if (!copy[selectedElement.id]) copy[selectedElement.id] = {};
                                                        copy[selectedElement.id].align = val;
                                                        return copy;
                                                    });
                                                }}
                                            >
                                                <option value="">Default</option>
                                                <option value="left">Left</option>
                                                <option value="center">Center</option>
                                                <option value="right">Right</option>
                                            </Dropdown>
                                            {selectedElement.id === "code" && (
                                                <TextField
                                                    label="Character Limit"
                                                    id="code_char_limit_trans"
                                                    placeholder="e.g. 8"
                                                    value={rawConfig[selectedElement.id]?.char_limit || ""}
                                                    onChange={e => {
                                                        const val = e.currentTarget.value;
                                                        setRawConfig((prev: any) => {
                                                            const copy = { ...prev };
                                                            if (!copy[selectedElement.id]) copy[selectedElement.id] = {};
                                                            copy[selectedElement.id].char_limit = val;
                                                            return copy;
                                                        });
                                                    }}
                                                />
                                            )}
                                            <Inline space="medium">
                                                <Checkbox
                                                    label="Bold"
                                                    id="sel_bold"
                                                    checked={getFieldStyle(selectedElement.id, "bold")}
                                                    onChange={() => handleFieldStyleChange(selectedElement.id, "bold", !getFieldStyle(selectedElement.id, "bold"))}
                                                />
                                                <Checkbox
                                                    label="Italic"
                                                    id="sel_italic"
                                                    checked={getFieldStyle(selectedElement.id, "italic")}
                                                    onChange={() => handleFieldStyleChange(selectedElement.id, "italic", !getFieldStyle(selectedElement.id, "italic"))}
                                                />
                                                <Checkbox
                                                    label="Translate"
                                                    id="sel_translate"
                                                    checked={getFieldStyle(selectedElement.id, "translate")}
                                                    onChange={() => handleFieldStyleChange(selectedElement.id, "translate", !getFieldStyle(selectedElement.id, "translate"))}
                                                />
                                                {(selectedElement.id.toLowerCase().includes('rate') || selectedElement.id.toLowerCase().includes('percent')) && (
                                                    <Checkbox
                                                        label="Hide % symbol"
                                                        id="sel_hide_percent"
                                                        checked={Boolean(getFieldStyle(selectedElement.id, "hide_percent"))}
                                                        onChange={() => handleFieldStyleChange(selectedElement.id, "hide_percent", !getFieldStyle(selectedElement.id, "hide_percent"))}
                                                    />
                                                )}
                                                {selectedElement.id.toLowerCase().includes('period') && (
                                                    <Checkbox
                                                        label="Hide 'Months' label"
                                                        id="sel_hide_months"
                                                        checked={Boolean(getFieldStyle(selectedElement.id, "hide_months"))}
                                                        onChange={() => handleFieldStyleChange(selectedElement.id, "hide_months", !getFieldStyle(selectedElement.id, "hide_months"))}
                                                    />
                                                )}
                                            </Inline>
                                            {selectedElement.id === 'opening_tag' && (
                                                <Card>
                                                    <Stack space="small">
                                                        <Text weight="strong" size="small">Opening Tag Text</Text>
                                                        <TextField
                                                            label="English"
                                                            id="opening_value_ln1"
                                                            value={rawConfig.opening_tag?.value_ln1 || ''}
                                                            placeholder="Opening Balance"
                                                            onChange={e => {
                                                                const val = e.currentTarget.value;
                                                                setRawConfig((prev: any) => {
                                                                    const copy = { ...prev };
                                                                    if (!copy.opening_tag) copy.opening_tag = {};
                                                                    copy.opening_tag.value_ln1 = val;
                                                                    return copy;
                                                                });
                                                            }}
                                                        />
                                                        <TextField
                                                            label="Sinhala"
                                                            id="opening_value_ln2"
                                                            value={rawConfig.opening_tag?.value_ln2 || ''}
                                                            placeholder="ආරම්භක ශේෂය"
                                                            onChange={e => {
                                                                const val = e.currentTarget.value;
                                                                setRawConfig((prev: any) => {
                                                                    const copy = { ...prev };
                                                                    if (!copy.opening_tag) copy.opening_tag = {};
                                                                    copy.opening_tag.value_ln2 = val;
                                                                    return copy;
                                                                });
                                                            }}
                                                        />
                                                        <TextField
                                                            label="Tamil"
                                                            id="opening_value_ln3"
                                                            value={rawConfig.opening_tag?.value_ln3 || ''}
                                                            placeholder="ஆரம்ப இருப்பு"
                                                            onChange={e => {
                                                                const val = e.currentTarget.value;
                                                                setRawConfig((prev: any) => {
                                                                    const copy = { ...prev };
                                                                    if (!copy.opening_tag) copy.opening_tag = {};
                                                                    copy.opening_tag.value_ln3 = val;
                                                                    return copy;
                                                                });
                                                            }}
                                                        />
                                                    </Stack>
                                                </Card>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <Heading level="3">Selected: {selectedElement.id === "table_section" ? "Table Section" : selectedElement.id.startsWith("table_section_") ? `Table Copy (${selectedElement.id.replace("table_section_table_copy_", "")})` : selectedElement.id} ({selectedElement.type === "label" ? "Label" : "Value"})</Heading>

                                            <Columns space="small">
                                                <Column>
                                                    <TextField
                                                        label="Top (cm)"
                                                        id="sel_top"
                                                        value={getSelectedCoordinate("top")}
                                                        onChange={e => handleSelectedCoordinateChange("top", e.currentTarget.value)}
                                                    />
                                                </Column>
                                                <Column>
                                                    <TextField
                                                        label="Left (cm)"
                                                        id="sel_left"
                                                        value={getSelectedCoordinate("left")}
                                                        onChange={e => handleSelectedCoordinateChange("left", e.currentTarget.value)}
                                                    />
                                                </Column>
                                            </Columns>

                                            {selectedElement.id !== "table_section" && !selectedElement.id.startsWith("table_section_") && (
                                                <>
                                                    <TextField
                                                        label="Font Size"
                                                        id="sel_font_size"
                                                        placeholder="e.g. 12px or 10pt"
                                                        value={getFieldStyle(selectedElement.id, "font_size")}
                                                        onChange={e => handleFieldStyleChange(selectedElement.id, "font_size", e.currentTarget.value)}
                                                    />
                                                    <Dropdown
                                                        label="Alignment"
                                                        id="sel_align"
                                                        value={rawConfig[selectedElement.id]?.align || ""}
                                                        onChange={e => {
                                                            const val = e.currentTarget.value;
                                                            setRawConfig((prev: any) => {
                                                                const copy = { ...prev };
                                                                if (!copy[selectedElement.id]) copy[selectedElement.id] = {};
                                                                copy[selectedElement.id].align = val;
                                                                return copy;
                                                            });
                                                        }}
                                                    >
                                                        <option value="">Default</option>
                                                        <option value="left">Left</option>
                                                        <option value="center">Center</option>
                                                        <option value="right">Right</option>
                                                    </Dropdown>
                                                    <Inline space="medium">
                                                        <Checkbox
                                                            label="Bold"
                                                            id="sel_bold"
                                                            checked={getFieldStyle(selectedElement.id, "bold")}
                                                            onChange={() => handleFieldStyleChange(selectedElement.id, "bold", !getFieldStyle(selectedElement.id, "bold"))}
                                                        />
                                                        <Checkbox
                                                            label="Italic"
                                                            id="sel_italic"
                                                            checked={getFieldStyle(selectedElement.id, "italic")}
                                                            onChange={() => handleFieldStyleChange(selectedElement.id, "italic", !getFieldStyle(selectedElement.id, "italic"))}
                                                        />
                                                        <Checkbox
                                                            label="Translate"
                                                            id="sel_translate"
                                                            checked={selectedElement.type === "value" ? getFieldStyle(selectedElement.id, "translate") : getFieldStyle(selectedElement.id, "label_translate")}
                                                            onChange={() => {
                                                                const key = selectedElement.type === "value" ? "translate" : "label_translate";
                                                                handleFieldStyleChange(selectedElement.id, key, !getFieldStyle(selectedElement.id, key));
                                                            }}
                                                        />
                                                        {(selectedElement.id.toLowerCase().includes('rate') || selectedElement.id.toLowerCase().includes('percent')) && (
                                                            <Checkbox
                                                                label="Hide % symbol"
                                                                id="sel_hide_percent"
                                                                checked={Boolean(getFieldStyle(selectedElement.id, "hide_percent"))}
                                                                onChange={() => handleFieldStyleChange(selectedElement.id, "hide_percent", !getFieldStyle(selectedElement.id, "hide_percent"))}
                                                            />
                                                        )}
                                                        {selectedElement.id.toLowerCase().includes('period') && (
                                                            <Checkbox
                                                                label="Hide 'Months' label"
                                                                id="sel_hide_months"
                                                                checked={Boolean(getFieldStyle(selectedElement.id, "hide_months"))}
                                                                onChange={() => handleFieldStyleChange(selectedElement.id, "hide_months", !getFieldStyle(selectedElement.id, "hide_months"))}
                                                            />
                                                        )}
                                                    </Inline>
                                                </>
                                            )}
                                        </>
                                    )}
                                </Stack>
                            </Card>
                        )}

                        <Card>
                            <Stack space="small">
                                <Heading level="3">3. Upload Scanned Template</Heading>
                                <input
                                    type="file"
                                    accept="image/*"
                                    ref={fileInputRef}
                                    style={{ display: "none" }}
                                    onChange={handleImageUpload}
                                />
                                <Button variant="ghost" onClick={() => fileInputRef.current?.click()}>
                                    Choose Local Image File
                                </Button>
                                {templateImage ? (
                                    <Stack space="xsmall">
                                        <Text tone="positive" size="xsmall">Template image loaded.</Text>
                                        <Button variant="ghost" tone="critical" onClick={() => {
                                            setTemplateImage(null);
                                            setRawConfig((prev: any) => {
                                                const copy = { ...prev };
                                                delete copy.background_image;
                                                return copy;
                                            });
                                        }}>
                                            Remove Background Image
                                        </Button>
                                    </Stack>
                                ) : null}
                                <Checkbox
                                    id="show-grid-toggle"
                                    label="Show Alignment Grid (1cm)"
                                    checked={showGrid}
                                    onChange={() => setShowGrid(!showGrid)}
                                />
                            </Stack>
                        </Card>

                        <Card>
                            <Stack space="small">
                                <Heading level="3">4. Custom Field Registry</Heading>
                                <Columns space="small" alignY="bottom">
                                    <Column>
                                        <TextField
                                            label="New Field Name"
                                            id="new-field-reg"
                                            value={newFieldName}
                                            onChange={e => setNewFieldName(e.currentTarget.value)}
                                            placeholder="e.g. signature_line"
                                        />
                                    </Column>
                                    <Column width="content">
                                        <Button onClick={handleAddCustomField}>Add</Button>
                                    </Column>
                                </Columns>
                            </Stack>
                        </Card>

                        <Card>
                            <Stack space="small">
                                <Heading level="3">5. Toggle Fields</Heading>
                                <TextField
                                    id="field-search"
                                    aria-label="Filter"
                                    placeholder="Search fields..."
                                    value={fieldSearch}
                                    onChange={e => setFieldSearch(e.currentTarget.value)}
                                />
                                <Box style={{ maxHeight: "400px", overflowY: "auto", paddingRight: "4px" }}>
                                    <Stack space="medium">
                                        {[...availableFields]
                                            .sort((a, b) => a.label.localeCompare(b.label))
                                            .filter(f => fieldSearch === "" || f.label.toLowerCase().includes(fieldSearch.toLowerCase()) || f.id.toLowerCase().includes(fieldSearch.toLowerCase()))
                                            .map(field => {
                                                const valVisible = isFieldVisible(field.id, "value");
                                                const lblVisible = isFieldVisible(field.id, "label");

                                                return (
                                                    <Box key={field.id} padding="xsmall" style={{ borderBottom: "1px solid #eee" }}>
                                                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                                            <Text weight="strong" size="small">{field.label}</Text>
                                                            <div style={{ display: "flex", gap: "4px" }}>
                                                                {category !== "TRANSACTIONS" && category !== "OPENING_BALANCE" && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleCopyField(field.id)}
                                                                        style={{
                                                                            padding: "2px 6px",
                                                                            fontSize: "10px",
                                                                            borderRadius: "4px",
                                                                            border: "1px solid #c8ccd4",
                                                                            backgroundColor: "#fff",
                                                                            color: "#2b303a",
                                                                            cursor: "pointer",
                                                                            fontWeight: 500
                                                                        }}
                                                                    >
                                                                        Copy
                                                                    </button>
                                                                )}
                                                                {field.id.includes("_copy_") && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleDeleteCopyField(field.id)}
                                                                        style={{
                                                                            padding: "2px 6px",
                                                                            fontSize: "10px",
                                                                            borderRadius: "4px",
                                                                            border: "1px solid #dc3545",
                                                                            backgroundColor: "#fff",
                                                                            color: "#dc3545",
                                                                            cursor: "pointer",
                                                                            fontWeight: 500
                                                                        }}
                                                                    >
                                                                        Delete
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <Box paddingTop="xsmall">
                                                            <Inline space="medium">
                                                                <Checkbox
                                                                    id={`val-${field.id}`}
                                                                    label={(category === "TRANSACTIONS" || category === "OPENING_BALANCE") ? "Show Column" : "Value"}
                                                                    checked={valVisible}
                                                                    onChange={() => {
                                                                        if (field.id.includes("_copy_") && valVisible) {
                                                                            handleDeleteCopyField(field.id);
                                                                        } else {
                                                                            toggleFieldVisibility(field.id, "value");
                                                                        }
                                                                    }}
                                                                />
                                                                {category !== "TRANSACTIONS" && category !== "OPENING_BALANCE" && (
                                                                    <Checkbox
                                                                        id={`lbl-${field.id}`}
                                                                        label="Label"
                                                                        checked={lblVisible}
                                                                        onChange={() => toggleFieldVisibility(field.id, "label")}
                                                                    />
                                                                )}
                                                            </Inline>
                                                        </Box>

                                                        {valVisible && category !== "TRANSACTIONS" && category !== "OPENING_BALANCE" && (
                                                            <Box paddingTop="small" paddingLeft="small" style={{ borderLeft: "2px solid #0d6efd", marginLeft: "4px" }}>
                                                                <Stack space="small">
                                                                    <Text weight="strong" size="xsmall" tone="secondary">Value Layout & Typography</Text>
                                                                    <Columns space="xsmall">
                                                                        <Column>
                                                                            <TextField
                                                                                label="Top (cm)"
                                                                                id={`val-top-${field.id}`}
                                                                                value={String(printType === "Transaction Receipt" ? (rawConfig.fields?.[field.id]?.top || "") : (rawConfig[`${field.id}_top`] || "")).replace("cm", "")}
                                                                                onChange={e => {
                                                                                    const v = e.currentTarget.value;
                                                                                    setRawConfig((prev: any) => {
                                                                                        const copy = { ...prev };
                                                                                        if (printType === "Transaction Receipt") {
                                                                                            if (!copy.fields) copy.fields = {};
                                                                                            if (!copy.fields[field.id]) copy.fields[field.id] = { top: "0", left: "0", visible: true };
                                                                                            copy.fields[field.id].top = v.replace("cm", "");
                                                                                        } else {
                                                                                            copy[`${field.id}_top`] = v === "" ? "" : v.endsWith("cm") ? v : v + "cm";
                                                                                        }
                                                                                        return copy;
                                                                                    });
                                                                                }}
                                                                            />
                                                                        </Column>
                                                                        <Column>
                                                                            <TextField
                                                                                label="Left (cm)"
                                                                                id={`val-left-${field.id}`}
                                                                                value={String(printType === "Transaction Receipt" ? (rawConfig.fields?.[field.id]?.left || "") : (rawConfig[`${field.id}_left`] || "")).replace("cm", "")}
                                                                                onChange={e => {
                                                                                    const v = e.currentTarget.value;
                                                                                    setRawConfig((prev: any) => {
                                                                                        const copy = { ...prev };
                                                                                        if (printType === "Transaction Receipt") {
                                                                                            if (!copy.fields) copy.fields = {};
                                                                                            if (!copy.fields[field.id]) copy.fields[field.id] = { top: "0", left: "0", visible: true };
                                                                                            copy.fields[field.id].left = v.replace("cm", "");
                                                                                        } else {
                                                                                            copy[`${field.id}_left`] = v === "" ? "" : v.endsWith("cm") ? v : v + "cm";
                                                                                        }
                                                                                        return copy;
                                                                                    });
                                                                                }}
                                                                            />
                                                                        </Column>
                                                                    </Columns>
                                                                    <Box paddingTop="xsmall">
                                                                        <TextField
                                                                            label="Font Size"
                                                                            id={`val-fs-${field.id}`}
                                                                            placeholder="e.g. 12px or 10pt"
                                                                            value={getFieldStyle(field.id, "font_size")}
                                                                            onChange={e => handleFieldStyleChange(field.id, "font_size", e.currentTarget.value)}
                                                                        />
                                                                    </Box>
                                                                    <Box paddingTop="xsmall">
                                                                        <Inline space="medium">
                                                                            <Checkbox
                                                                                id={`val-bold-${field.id}`}
                                                                                label="Bold"
                                                                                checked={getFieldStyle(field.id, "bold")}
                                                                                onChange={() => handleFieldStyleChange(field.id, "bold", !getFieldStyle(field.id, "bold"))}
                                                                            />
                                                                            <Checkbox
                                                                                id={`val-italic-${field.id}`}
                                                                                label="Italic"
                                                                                checked={getFieldStyle(field.id, "italic")}
                                                                                onChange={() => handleFieldStyleChange(field.id, "italic", !getFieldStyle(field.id, "italic"))}
                                                                            />
                                                                            <Checkbox
                                                                                id={`val-translate-${field.id}`}
                                                                                label="Translate"
                                                                                checked={getFieldStyle(field.id, "translate")}
                                                                                onChange={() => handleFieldStyleChange(field.id, "translate", !getFieldStyle(field.id, "translate"))}
                                                                            />
                                                                        </Inline>
                                                                    </Box>
                                                                </Stack>
                                                            </Box>
                                                        )}

                                                        {lblVisible && category !== "TRANSACTIONS" && category !== "OPENING_BALANCE" && (
                                                            <Box paddingTop="small" paddingLeft="small" style={{ borderLeft: "2px solid #28a745", marginLeft: "4px", marginTop: "4px" }}>
                                                                <Stack space="small">
                                                                    <Text weight="strong" size="xsmall" tone="secondary">Label Layout & Typography</Text>
                                                                    <Columns space="xsmall">
                                                                        <Column>
                                                                            <TextField
                                                                                label="Label Top (cm)"
                                                                                id={`lbl-top-${field.id}`}
                                                                                value={String(printType === "Transaction Receipt" ? (rawConfig.fields?.[field.id]?.label_top || "") : (rawConfig[`${field.id}_label_top`] || "")).replace("cm", "")}
                                                                                onChange={e => {
                                                                                    const v = e.currentTarget.value;
                                                                                    setRawConfig((prev: any) => {
                                                                                        const copy = { ...prev };
                                                                                        if (printType === "Transaction Receipt") {
                                                                                            if (!copy.fields) copy.fields = {};
                                                                                            if (!copy.fields[field.id]) copy.fields[field.id] = { top: "0", left: "0", visible: true };
                                                                                            copy.fields[field.id].label_top = v.replace("cm", "");
                                                                                        } else {
                                                                                            copy[`${field.id}_label_top`] = v === "" ? "" : v.endsWith("cm") ? v : v + "cm";
                                                                                        }
                                                                                        return copy;
                                                                                    });
                                                                                }}
                                                                            />
                                                                        </Column>
                                                                        <Column>
                                                                            <TextField
                                                                                label="Label Left (cm)"
                                                                                id={`lbl-left-${field.id}`}
                                                                                value={String(printType === "Transaction Receipt" ? (rawConfig.fields?.[field.id]?.label_left || "") : (rawConfig[`${field.id}_label_left`] || "")).replace("cm", "")}
                                                                                onChange={e => {
                                                                                    const v = e.currentTarget.value;
                                                                                    setRawConfig((prev: any) => {
                                                                                        const copy = { ...prev };
                                                                                        if (printType === "Transaction Receipt") {
                                                                                            if (!copy.fields) copy.fields = {};
                                                                                            if (!copy.fields[field.id]) copy.fields[field.id] = { top: "0", left: "0", visible: true };
                                                                                            copy.fields[field.id].label_left = v.replace("cm", "");
                                                                                        } else {
                                                                                            copy[`${field.id}_label_left`] = v === "" ? "" : v.endsWith("cm") ? v : v + "cm";
                                                                                        }
                                                                                        return copy;
                                                                                    });
                                                                                }}
                                                                            />
                                                                        </Column>
                                                                    </Columns>
                                                                    <Box paddingTop="small">
                                                                        <Stack space="xsmall">
                                                                            <TextField
                                                                                label="Label Text (English)"
                                                                                id={`lbl-en-${field.id}`}
                                                                                value={getCustomLabelValue(field.id, "en")}
                                                                                onChange={e => handleCustomLabelChange(field.id, "en", e.currentTarget.value)}
                                                                            />
                                                                            <TextField
                                                                                label="Label Text (Sinhala)"
                                                                                id={`lbl-si-${field.id}`}
                                                                                value={getCustomLabelValue(field.id, "si")}
                                                                                onChange={e => handleCustomLabelChange(field.id, "si", e.currentTarget.value)}
                                                                            />
                                                                            <TextField
                                                                                label="Label Text (Tamil)"
                                                                                id={`lbl-ta-${field.id}`}
                                                                                value={getCustomLabelValue(field.id, "ta")}
                                                                                onChange={e => handleCustomLabelChange(field.id, "ta", e.currentTarget.value)}
                                                                            />
                                                                            <Box paddingTop="xsmall">
                                                                                <Checkbox
                                                                                    id={`lbl-translate-${field.id}`}
                                                                                    label="Translate"
                                                                                    checked={getFieldStyle(field.id, "label_translate")}
                                                                                    onChange={() => handleFieldStyleChange(field.id, "label_translate", !getFieldStyle(field.id, "label_translate"))}
                                                                                />
                                                                            </Box>
                                                                        </Stack>
                                                                    </Box>
                                                                </Stack>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                );
                                            })}
                                    </Stack>
                                </Box>
                            </Stack>
                        </Card>

                        <Card>
                            <Stack space="small">
                                <Heading level="3">6. Layout Database Backup / Restore</Heading>
                                <Text size="small" tone="secondary">Backup or restore all layout configurations from the database.</Text>
                                <Button
                                    tone="brandAccent"
                                    onClick={handleBackup}
                                    loading={isBackingUp}
                                >
                                    Backup Layouts (SQL)
                                </Button>
                                <Box paddingTop="small" style={{ borderTop: "1px solid #eee" }}>
                                    <Stack space="small">
                                        <Dropdown
                                            label="Select Backup to Import"
                                            id="select_layout_backup"
                                            value={selectedBackup}
                                            onChange={e => setSelectedBackup(e.currentTarget.value)}
                                        >
                                            {backups.length === 0 ? (
                                                <option value="">No backups found</option>
                                            ) : (
                                                backups.map(file => (
                                                    <option key={file} value={file}>{file}</option>
                                                ))
                                            )}
                                        </Dropdown>
                                        <Button
                                            variant="ghost"
                                            tone="critical"
                                            onClick={handleRestore}
                                            loading={isRestoring}
                                        >
                                            Import Selected Backup
                                        </Button>
                                    </Stack>
                                </Box>
                            </Stack>
                        </Card>

                    </Stack>
                </Column>

                {/* Right Visual Work Area Canvas */}
                <Column width="2/3">
                    <Box padding="medium" background="surface" borderRadius="standard" style={{ minHeight: "100%", background: "#e9ecef", overflow: "auto", width: "100%" }}>
                        <Stack space="medium" align="center">

                            <Text tone="secondary" size="small">
                                Click and drag items on top of the scanned template. Active coordinates are calculated and saved in centimeters.
                            </Text>

                            <div style={{ display: 'flex', alignItems: 'flex-start', position: 'relative', flexShrink: 0 }}>
                                {/* Canvas Container — exact cm size matches PassBookPrint */}
                                <Box
                                    style={{
                                        width: `${pageWidthNum}cm`,
                                        height: `${pageHeightNum}cm`,
                                        minWidth: `${pageWidthNum}cm`,
                                        minHeight: `${pageHeightNum}cm`,
                                        flexShrink: 0,
                                        position: "relative",
                                        border: "2px solid #6c757d",
                                        borderRadius: "4px",
                                        backgroundImage: canvasBgImage,
                                        backgroundSize: canvasBgSize,
                                        backgroundPosition: canvasBgPosition,
                                        backgroundRepeat: canvasBgRepeat,
                                        backgroundColor: "#ffffff",
                                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                        overflow: "hidden",
                                        boxSizing: "border-box"
                                    }}
                                >
                                    {!templateImage && (
                                        <Box
                                            style={{
                                                position: "absolute",
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                bottom: 0,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                backgroundColor: "rgba(255,255,255,0.7)"
                                            }}
                                        >
                                            <Text tone="secondary">Scanned Background Template Preview Area</Text>
                                        </Box>
                                    )}

                                    {alignmentLines.x !== null && (
                                        <div
                                            style={{
                                                position: "absolute",
                                                left: `${alignmentLines.x}px`,
                                                top: 0,
                                                bottom: 0,
                                                width: "1px",
                                                borderLeft: "1px dashed #fd7e14",
                                                zIndex: 99,
                                                pointerEvents: "none"
                                            }}
                                        />
                                    )}
                                    {alignmentLines.y !== null && (
                                        <div
                                            style={{
                                                position: "absolute",
                                                top: `${alignmentLines.y}px`,
                                                left: 0,
                                                right: 0,
                                                height: "1px",
                                                borderTop: "1px dashed #fd7e14",
                                                zIndex: 99,
                                                pointerEvents: "none"
                                            }}
                                        />
                                    )}

                                    {/* Mock Table Section Draggable Box */}
                                    {(printType === "Transaction Receipt" || category === "PAWNING_MANDATE") && (() => {
                                        const sections = rawConfig.sections || {};
                                        const tableKeys = ["table", ...Object.keys(sections).filter(k => k.startsWith("table_copy_"))];

                                        return tableKeys.map((tableKey) => {
                                            const tableConfig = sections[tableKey];
                                            if (tableKey !== "table" && !tableConfig) return null;

                                            // Determine visibility/enabled
                                            const isEnabled = tableConfig?.enabled !== false;
                                            if (!isEnabled) return null;

                                            const tableId = tableKey === "table" ? "table_section" : `table_section_${tableKey}`;
                                            const isSelected = selectedElement?.id === tableId;

                                            const tTop = tableConfig?.top || "7.5cm";
                                            const tLeft = tableConfig?.left || "2cm";
                                            const tWidth = tableConfig?.width || "15cm";

                                            const tTopCm = getCoordinateValue(tTop);
                                            const tLeftCm = getCoordinateValue(tLeft);
                                            const tWidthCmVal = getCoordinateValue(tWidth);

                                            const activeCols = tableConfig?.columns || getDefaultTableColumns(printType, category);

                                            const tableAutoFitLocal = (printType === "Transaction Receipt" || category === "PAWNING_MANDATE") ? false : (tableConfig?.auto_fit !== 'false');

                                            const configHeight = tableConfig?.height;
                                            const displayHeight = configHeight ? getCoordinateValue(configHeight) : tableHeightPx;

                                            return (
                                                <div
                                                    key={tableId}
                                                    onPointerDown={e => handleTablePointerDown(e, tableId)}
                                                    style={{
                                                        position: "absolute",
                                                        left: `${tLeftCm}cm`,
                                                        top: `${tTopCm}cm`,
                                                        width: `${tableAutoFitLocal ? tableWidthPx : tWidthCmVal}cm`,
                                                        height: `${displayHeight}cm`,
                                                        cursor: "move",
                                                        willChange: "transform",
                                                        border: isSelected ? "2px dashed #0d6efd" : "1px dashed #dc3545",
                                                        backgroundColor: isSelected ? "rgba(13, 110, 253, 0.08)" : "rgba(220, 53, 69, 0.08)",
                                                        boxSizing: "border-box",
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        zIndex: isSelected ? 10 : 1,
                                                        userSelect: "none"
                                                    }}
                                                >
                                                    {/* Render a mock table header */}
                                                    {!tableConfig?.hide_header && (
                                                        <div style={{ display: "flex", borderBottom: "1px solid #dc3545", fontSize: "8px", fontWeight: "bold", padding: "2px" }}>
                                                            {activeCols.map((colId: string) => {
                                                                const colLabel = getAvailableTableColumns(printType, category).find(c => c.id === colId)?.label || colId;

                                                                return (
                                                                    <div key={colId} style={{
                                                                        position: "relative",
                                                                        width: getColWidthPx(colId, tableId),
                                                                        textAlign: (tableConfig?.[`${colId}_align`] || (["amount", "balance", "debit", "credit", "accessed_value", "market_value", "capital"].includes(colId) ? "right" : "left")) as any,
                                                                        fontSize: tableConfig?.[`${colId}_font_size`] || "8px",
                                                                        fontWeight: tableConfig?.[`${colId}_bold`] === "true" ? "bold" : "bold",
                                                                        fontStyle: tableConfig?.[`${colId}_italic`] === "true" ? "italic" : "normal"
                                                                    }}>
                                                                        {colLabel}
                                                                        <div
                                                                            onPointerDown={e => handleTransactionColResizePointerDown(e, colId, tableId)}
                                                                            style={{
                                                                                position: "absolute",
                                                                                top: 0,
                                                                                right: 0,
                                                                                width: "6px",
                                                                                height: "100%",
                                                                                cursor: "col-resize",
                                                                                backgroundColor: "transparent",
                                                                                zIndex: 10
                                                                            }}
                                                                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#0d6efd" }}
                                                                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent" }}
                                                                        />
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                    {/* Render a mock table row */}
                                                    <div style={{ display: "flex", padding: "2px", opacity: 0.7 }}>
                                                        {activeCols.map((colId: string) => {
                                                            const mockVal = category === "PAWNING_MANDATE" ? {
                                                                name: "Gold Ring",
                                                                description: "22K gold ring with red stone",
                                                                quantity: "1",
                                                                carat: "22",
                                                                weight_gram: "8.0",
                                                                gold_weight_gram: "7.5",
                                                                accessed_value: "150,000.00",
                                                                market_value: "165,000.00",
                                                                capital: "100,000.00"
                                                            }[colId] || "" : {
                                                                account_number: "1001-xxx",
                                                                account_name: "Savings Acc",
                                                                amount: "5,000.00",
                                                                debit: "5,000.00",
                                                                credit: "0.00",
                                                                description: "Mock Tx 1",
                                                                cheque_num: "123456"
                                                            }[colId] || "";

                                                            return (
                                                                <div key={colId} style={{
                                                                    width: getColWidthPx(colId, tableId),
                                                                    textAlign: (tableConfig?.[`${colId}_align`] || (["amount", "balance", "debit", "credit", "accessed_value", "market_value", "capital"].includes(colId) ? "right" : "left")) as any,
                                                                    fontSize: tableConfig?.[`${colId}_font_size`] || "8px",
                                                                    fontWeight: tableConfig?.[`${colId}_bold`] === "true" ? "bold" : "normal",
                                                                    fontStyle: tableConfig?.[`${colId}_italic`] === "true" ? "italic" : "normal"
                                                                }}>{mockVal}</div>
                                                            );
                                                        })}
                                                    </div>
                                                    <div style={{ fontSize: "8px", fontStyle: "italic", color: isSelected ? "#0d6efd" : "#dc3545", textAlign: "center", marginTop: "auto" }}>
                                                        {tableKey === "table" ? "Table Section" : `Table Copy (${tableKey.replace("table_copy_", "")})`} ({tTop}, {tLeft})
                                                    </div>

                                                    {/* Right-edge resize handle (only when auto-fit is disabled) */}
                                                    {!tableAutoFitLocal && (
                                                        <div
                                                            onPointerDown={e => handleTableResizePointerDown(e, tableId)}
                                                            style={{
                                                                position: 'absolute',
                                                                right: 0,
                                                                top: 0,
                                                                bottom: 0,
                                                                width: '6px',
                                                                cursor: 'ew-resize',
                                                                background: 'rgba(0,0,0,0.06)'
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        });
                                    })()}
                                    {/* Mock Transactions Columns (Passbook/Passcard) */}
                                    {(category === "TRANSACTIONS" || category === "OPENING_BALANCE") && (
                                        <>
                                            {getRequiredFields().map((fieldId: string) => {
                                                const fieldConfig = rawConfig[fieldId] || {};
                                                const activeFields = category === "OPENING_BALANCE" ? openingBalanceFields : passbookTransactionFields;
                                                const label = activeFields.find(f => f.id === fieldId)?.label || fieldId;
                                                const innerWidth = getNormalizedColumnWidth(fieldConfig.column_width, fieldId, tableWidthCm);
                                                const leftPos = getFieldLeftCm(fieldId);
                                                const bold = fieldConfig.bold === true;
                                                const italic = fieldConfig.italic === true;
                                                const fontSize = formatFontSize(fieldConfig.font_size) !== 'inherit' ? formatFontSize(fieldConfig.font_size) : formatGlobalFontSize(rawConfig.font_size);
                                                const align = fieldConfig.align || (['debit', 'credit', 'balance', 'interest', 'entered_by', 'entered_by_code', 'updated_by', 'updated_by_code', 'amount'].includes(fieldId) ? 'right' : 'center');
                                                const isSelected = selectedElement?.id === fieldId;

                                                return (
                                                    <div
                                                        key={fieldId}
                                                        onPointerDown={e => handlePointerDown(e, fieldId, "value")}
                                                        style={{
                                                            position: "absolute",
                                                            left: leftPos,
                                                            width: innerWidth,
                                                            top: `${marginTopCm}cm`,
                                                            height: `${lineHeightCm * 4}cm`, // 1 header row + 3 mock rows
                                                            boxSizing: "border-box",
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            fontFamily: "monospace",
                                                            border: isSelected ? "2px dashed #0d6efd" : "1px solid rgba(0,0,0,0.15)",
                                                            backgroundColor: isSelected ? "rgba(13, 110, 253, 0.05)" : "transparent",
                                                            cursor: "grab",
                                                            zIndex: isSelected ? 10 : 1
                                                        }}
                                                    >
                                                        {/* Header Cell */}
                                                        <div style={{
                                                            height: `${lineHeightCm}cm`,
                                                            borderBottom: "2px solid #333",
                                                            fontSize: "10px",
                                                            fontWeight: "bold",
                                                            textAlign: align as any,
                                                            padding: "4px",
                                                            boxSizing: "border-box",
                                                            whiteSpace: "nowrap",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis"
                                                        }}>
                                                            {label}
                                                        </div>
                                                        {/* Mock Data Rows */}
                                                        {[1, 2, 3].map((rowNum) => {
                                                            let cellText = "";
                                                            if (fieldId === "no") cellText = String(rowNum);
                                                            else if (fieldId === "entered_date") {
                                                                const fmt = rawConfig.date_format || "yyyy-MM-dd";
                                                                if (fmt.toLowerCase() === "yy-mm-dd") cellText = "26-06-09";
                                                                else if (fmt.toLowerCase() === "dd-mm-yyyy") cellText = "09-06-2026";
                                                                else if (fmt.toLowerCase() === "dd-mm-yy") cellText = "09-06-26";
                                                                else cellText = "2026-06-09";
                                                            }
                                                            else if (fieldId === "code") cellText = "DEP";
                                                            else if (fieldId === "description") cellText = `Mock Tx ${rowNum}`;
                                                            else if (fieldId === "debit") cellText = rowNum === 2 ? "* 1,000.00" : "";
                                                            else if (fieldId === "credit") cellText = rowNum !== 2 ? "* 5,000.00" : "";
                                                            else if (fieldId === "balance") cellText = rowNum === 1 ? "* 5,000.00" : rowNum === 2 ? "* 10,000.00" : "* 15,000.00";
                                                            else if (fieldId === "interest") cellText = rowNum === 3 ? "* 120.50" : "";
                                                            else if (fieldId === "penalty") cellText = rowNum === 2 ? "* 50.00" : "";
                                                            else if (fieldId === "fund") cellText = rowNum === 1 ? "* 100.00" : "";
                                                            else if (fieldId === "passdue") cellText = rowNum === 3 ? "* 200.00" : "";
                                                            else if (fieldId === "amount") cellText = rowNum !== 2 ? "* 5,000.00" : "";
                                                            else if (fieldId === "account_number") cellText = "001-001-001";
                                                            else if (fieldId === "account_name") cellText = "Savings Account";
                                                            else if (fieldId === "cheque_num") cellText = rowNum === 1 ? "12345" : "";
                                                            else if (fieldId === "entered_by") cellText = "admin";

                                                            return (
                                                                <div
                                                                    key={rowNum}
                                                                    style={{
                                                                        height: `${lineHeightCm}cm`,
                                                                        borderBottom: "1px solid #eee",
                                                                        textAlign: align as any,
                                                                        fontWeight: bold ? "bold" : "normal",
                                                                        fontStyle: italic ? "italic" : "normal",
                                                                        fontSize: fontSize,
                                                                        padding: "4px",
                                                                        boxSizing: "border-box",
                                                                        whiteSpace: "nowrap",
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis"
                                                                    }}
                                                                >
                                                                    {cellText}
                                                                </div>
                                                            );
                                                        })}
                                                        {/* Resize Handle on the right edge */}
                                                        <div
                                                            onMouseDown={(e) => handleColResizeMouseDown(e, fieldId)}
                                                            style={{
                                                                position: "absolute",
                                                                top: 0,
                                                                right: 0,
                                                                width: "6px",
                                                                height: "100%",
                                                                cursor: "col-resize",
                                                                backgroundColor: "transparent",
                                                                zIndex: 20
                                                            }}
                                                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#0d6efd" }}
                                                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent" }}
                                                        />
                                                    </div>
                                                );
                                            })}
                                        </>
                                    )}

                                    {/* Overlay Draggable Elements */}
                                    {category !== "TRANSACTIONS" && category !== "OPENING_BALANCE" && availableFields.map(field => {
                                        const valVisible = isFieldVisible(field.id, "value");
                                        const lblVisible = isFieldVisible(field.id, "label");

                                        // Extract positions
                                        let vL = "0cm", vT = "0cm", lL = "0cm", lT = "0cm";
                                        if (printType === "Transaction Receipt") {
                                            const f = rawConfig.fields?.[field.id];
                                            if (f) {
                                                vL = (f.left || "0") + "cm";
                                                vT = (f.top || "0") + "cm";
                                                lL = (f.label_left || "0") + "cm";
                                                lT = (f.label_top || "0") + "cm";
                                            }
                                        } else {
                                            vL = rawConfig[`${field.id}_left`] || "0cm";
                                            vT = rawConfig[`${field.id}_top`] || "0cm";
                                            lL = rawConfig[`${field.id}_label_left`] || "0cm";
                                            lT = rawConfig[`${field.id}_label_top`] || "0cm";
                                        }

                                        // Positions are kept in cm — used directly as CSS cm values
                                        const valX = getCoordinateValue(vL);
                                        const valY = getCoordinateValue(vT);
                                        const lblX = getCoordinateValue(lL);
                                        const lblY = getCoordinateValue(lT);

                                        const isValSelected = selectedElement?.id === field.id && selectedElement?.type === "value";
                                        const isLblSelected = selectedElement?.id === field.id && selectedElement?.type === "label";

                                        return (
                                            <React.Fragment key={`drag-${field.id}`}>
                                                {/* Draggable Value Box */}
                                                {valVisible && (
                                                    <div
                                                        onPointerDown={e => handlePointerDown(e, field.id, "value")}
                                                        style={{
                                                            position: "absolute",
                                                            left: `${valX}cm`,
                                                            top: `${valY}cm`,
                                                            cursor: "move",
                                                            willChange: "transform",
                                                            padding: "0.1cm 0.2cm",
                                                            background: isValSelected ? "rgba(13, 110, 253, 0.9)" : "rgba(33, 37, 41, 0.8)",
                                                            color: "#ffffff",
                                                            border: isValSelected ? "1px solid #fff" : "1px solid transparent",
                                                            borderRadius: "3px",
                                                            fontSize: formatFontSize(getFieldStyle(field.id, "font_size")) !== 'inherit' ? formatFontSize(getFieldStyle(field.id, "font_size")) : "10px",
                                                            fontWeight: getFieldStyle(field.id, "bold") ? "bold" : "normal",
                                                            fontStyle: getFieldStyle(field.id, "italic") ? "italic" : "normal",
                                                            userSelect: "none",
                                                            whiteSpace: "nowrap",
                                                            zIndex: isValSelected ? 10 : 2,
                                                            boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                                                        }}
                                                    >
                                                        {getFieldDisplayValue(field.id)}
                                                        {isValSelected && (
                                                            <span style={{ fontSize: "8px", display: "block", color: "#e9ecef" }}>
                                                                {vL}, {vT}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}

                                                {/* Draggable Label Box */}
                                                {lblVisible && (
                                                    <div
                                                        onPointerDown={e => handlePointerDown(e, field.id, "label")}
                                                        style={{
                                                            position: "absolute",
                                                            left: `${lblX}cm`,
                                                            top: `${lblY}cm`,
                                                            cursor: "move",
                                                            willChange: "transform",
                                                            padding: "0.1cm 0.2cm",
                                                            background: isLblSelected ? "rgba(40, 167, 69, 0.9)" : "rgba(108, 117, 125, 0.85)",
                                                            color: "#ffffff",
                                                            border: isLblSelected ? "1px solid #fff" : "1px solid transparent",
                                                            borderRadius: "3px",
                                                            fontSize: formatFontSize(getFieldStyle(field.id, "font_size")) !== 'inherit' ? formatFontSize(getFieldStyle(field.id, "font_size")) : "10px",
                                                            fontWeight: getFieldStyle(field.id, "bold") ? "bold" : "normal",
                                                            fontStyle: getFieldStyle(field.id, "italic") ? "italic" : "normal",
                                                            userSelect: "none",
                                                            whiteSpace: "nowrap",
                                                            zIndex: isLblSelected ? 10 : 2,
                                                            boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                                                        }}
                                                    >
                                                        Label: {getCustomLabelValue(field.id, "en") || field.id}
                                                        {isLblSelected && (
                                                            <span style={{ fontSize: "8px", display: "block", color: "#e9ecef" }}>
                                                                {lL}, {lT}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}

                                </Box>
                            </div>

                            <Box display="flex" justifyContent="center" paddingTop="medium" width="full">
                                <Button tone="brandAccent" onClick={handleSave} loading={saving}>
                                    Save Design
                                </Button>
                            </Box>

                        </Stack>
                    </Box>
                </Column>

            </Columns>
        </Box>
    );
};

export default PrintDesigner;
